import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import { useWorkspace } from "../../contexts/Workspacecontext";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import "./Candidates.scss";
import CandidateJobsPopup from "./CandidateJobsPopup";
import {
  CandidateTimelinePanel,
  ScheduleModal,
} from "../Interviews/InterviewPipeline";
import ScheduleInterviewPage from "./ScheduleInterviewPage";
import { useAuth } from "../../contexts/AuthContext";
import { ShareModal } from "../Consultancy/ShareRequirement";

// ─── API URLs ─────────────────────────────────────────────────────────────────
const COMPANY_API = `${process.env.REACT_APP_API_HR_URL}/recruitment/candidate`;
const CONN_API = `${process.env.REACT_APP_API_HR_URL}/recruitment/candidate/consultancy-candidates`;
const CONSULTANT_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/candidate`;
const APPLIED_API = `${process.env.REACT_APP_API_HR_URL}/candidate/applied/jobpost/all`;

const SHARED_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy`;
const APPLIED_CANDIDATES_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/applied-candidates`;
const RECRUITER_APPLIED_API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/applied-candidates/recruiter/job`;

// ─── Qualification types ──────────────────────────────────────────────────────
const QUALIFICATION_TYPES = [
  ["SCHOOL", "School"],
  ["PRE_UNIVERSITY", "Pre-University"],
  ["BACHELOR", "Bachelor's Degree"],
  ["MASTER", "Master's Degree"],
  ["PHD", "PhD / Doctorate"],
  ["OTHER", "Other"],
];

const QUALIFICATION_OPTIONS = {
  SCHOOL: ["10th Standard", "12th Standard"],
  PRE_UNIVERSITY: ["Diploma", "ITI", "Polytechnic"],
  BACHELOR: [
    "B.E / B.Tech",
    "B.Sc",
    "B.Com",
    "B.A",
    "BBA",
    "BCA",
    "B.Arch",
    "B.Pharm",
    "MBBS",
    "Other",
  ],
  MASTER: [
    "M.E / M.Tech",
    "M.Sc",
    "M.Com",
    "M.A",
    "MBA",
    "MCA",
    "M.Pharm",
    "MD",
    "Other",
  ],
  PHD: ["Ph.D", "D.Sc", "Other"],
  OTHER: ["Other"],
};

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { key: "default", label: "Default" },
  { key: "newest", label: "Newest First" },
  { key: "oldest", label: "Oldest First" },
  { key: "az", label: "A → Z" },
  { key: "za", label: "Z → A" },
  { key: "salary_desc", label: "Salary ↓" },
  { key: "salary_asc", label: "Salary ↑" },
  { key: "exp_desc", label: "Experience ↓" },
  { key: "exp_asc", label: "Experience ↑" },
];

// getCandidateAppliedDate — resolves the best available "applied" timestamp
// for a candidate row, regardless of which API it came from:
//  - candidate/applied/jobpost/all & consultancy-candidates → top-level `appliedOn`
//  - candidate/all → nested `appliedJobs[].appliedDate` (most recent of the list)
//  - fallback → `createdAt`
// Returns a numeric timestamp (ms) so callers can subtract directly; missing/
// invalid dates resolve to 0 so they sink to the bottom under "Newest First".
const getCandidateAppliedDate = (c = {}) => {
  // Guard against null, undefined, AND empty string (which ?? wouldn't catch)
  if (c.appliedOn && typeof c.appliedOn === "string" && c.appliedOn.trim()) {
    const t = new Date(c.appliedOn).getTime();
    if (!Number.isNaN(t)) return t;
  }

  const jobDates = (c.appliedJobs || [])
    .map((j) => j.appliedDate)
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));
  if (jobDates.length) return Math.max(...jobDates);

  if (c.createdAt) {
    const t = new Date(c.createdAt).getTime();
    if (!Number.isNaN(t)) return t;
  }

  return 0;
};

// formatDate — safe display formatter for ISO date/datetime strings.
// Returns "—" for missing/invalid values instead of "Invalid Date".
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// applySorting — pure function, takes filtered array + sortKey, returns sorted copy
const applySorting = (list, sortKey) => {
  if (!sortKey || sortKey === "default") return list;
  const sorted = [...list];
  switch (sortKey) {
    case "az":
      return sorted.sort((a, b) =>
        (a.candidateName || a.name || "")
          .trim()
          .toLowerCase()
          .localeCompare(
            (b.candidateName || b.name || "").trim().toLowerCase(),
          ),
      );
    case "za":
      return sorted.sort((a, b) =>
        (b.candidateName || b.name || "")
          .trim()
          .toLowerCase()
          .localeCompare(
            (a.candidateName || a.name || "").trim().toLowerCase(),
          ),
      );
    case "salary_asc":
      return sorted.sort(
        (a, b) => Number(a.expectedSalary || 0) - Number(b.expectedSalary || 0),
      );
    case "salary_desc":
      return sorted.sort(
        (a, b) => Number(b.expectedSalary || 0) - Number(a.expectedSalary || 0),
      );
    case "exp_asc":
      return sorted.sort(
        (a, b) => Number(a.experience || 0) - Number(b.experience || 0),
      );
    case "exp_desc":
      return sorted.sort(
        (a, b) => Number(b.experience || 0) - Number(a.experience || 0),
      );
    case "newest":
      return sorted.sort(
        (a, b) => getCandidateAppliedDate(b) - getCandidateAppliedDate(a),
      );
    case "oldest":
      return sorted.sort(
        (a, b) => getCandidateAppliedDate(a) - getCandidateAppliedDate(b),
      );
    default:
      return sorted;
  }
};

// ─── SortBar component ────────────────────────────────────────────────────────
const SortBar = ({ sortBy, setSortBy, options = SORT_OPTIONS }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const active = options.find((o) => o.key === sortBy && sortBy !== "default");

  return (
    <div className="cand-dropdown" ref={ref}>
      <button
        className={`cand-dropdown__trigger${active ? " cand-dropdown__trigger--active" : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M1 2.5h11M3 6.5h7M5 10.5h3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
        Sort{active ? `: ${active.label}` : ""}
        {active && (
          <span
            className="cand-dropdown__clear"
            onClick={(e) => {
              e.stopPropagation();
              setSortBy("default");
            }}
          >
            ×
          </span>
        )}
        <svg
          className={`cand-dropdown__caret${open ? " cand-dropdown__caret--open" : ""}`}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="cand-dropdown__menu">
          {options.map((opt) => (
            <button
              key={opt.key}
              className={`cand-dropdown__item${sortBy === opt.key ? " cand-dropdown__item--active" : ""}`}
              onClick={() => {
                setSortBy(opt.key === sortBy ? "default" : opt.key);
                setOpen(false);
              }}
            >
              {opt.label}
              {sortBy === opt.key && opt.key !== "default" && (
                <span style={{ marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterDropdown = ({ hasActiveFilters, onClear, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="cand-dropdown" ref={ref}>
      <button
        className={`cand-dropdown__trigger${hasActiveFilters ? " cand-dropdown__trigger--active" : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path
            d="M1 3h11M3 6.5h7M5 10h3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
        Filters{hasActiveFilters ? " •" : ""}
        {hasActiveFilters && (
          <span
            className="cand-dropdown__clear"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
          >
            ×
          </span>
        )}
        <svg
          className={`cand-dropdown__caret${open ? " cand-dropdown__caret--open" : ""}`}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="cand-dropdown__menu cand-dropdown__menu--wide">
          <div style={{ padding: "10px 12px" }}>{children}</div>
        </div>
      )}
    </div>
  );
};

// ─── MultiSelectDropdown ──────────────────────────────────────────────────────
const MultiSelectDropdown = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const displayLabel =
    selected.length === 0
      ? placeholder || `Any ${label}`
      : selected.length === 1
        ? options.find((o) => (Array.isArray(o) ? o[0] : o) === selected[0])
          ? Array.isArray(options[0])
            ? options.find((o) => o[0] === selected[0])?.[1] || selected[0]
            : selected[0]
          : selected[0]
        : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className={`cand-input`}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          background: selected.length > 0 ? "#eff6ff" : "",
          borderColor: selected.length > 0 ? "#bfdbfe" : "",
          color: selected.length > 0 ? "#1d4ed8" : "",
          fontWeight: selected.length > 0 ? 500 : "",
          textAlign: "left",
          gap: 4,
        }}
        onClick={() => setOpen((p) => !p)}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayLabel}
        </span>
        <svg
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
        >
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            zIndex: 200,
            maxHeight: 220,
            overflowY: "auto",
            padding: "4px 0",
          }}
        >
          {selected.length > 0 && (
            <button
              type="button"
              style={{
                width: "100%",
                padding: "6px 12px",
                textAlign: "left",
                fontSize: "0.78rem",
                color: "#ef4444",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
              }}
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
            >
              ✕ Clear all
            </button>
          )}
          {options.map((opt) => {
            const val = Array.isArray(opt) ? opt[0] : opt;
            const lbl = Array.isArray(opt) ? opt[1] : opt;
            const checked = selected.includes(val);
            return (
              <label
                key={val}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  background: checked ? "#eff6ff" : "none",
                  fontSize: "0.825rem",
                  color: checked ? "#1d4ed8" : "#374151",
                  fontWeight: checked ? 500 : 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(val)}
                  style={{
                    accentColor: "#2563eb",
                    width: 13,
                    height: 13,
                    flexShrink: 0,
                  }}
                />
                {lbl}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Shared UI helpers ────────────────────────────────────────────────────────
const F = ({ label, required, error, children }) => (
  <div className={`cand-field${error ? " cand-field--error" : ""}`}>
    <label className="cand-field__label">
      {label}
      {required && <span className="cand-field__req"> *</span>}
    </label>
    {children}
    {error && <span className="cand-field__error">{error}</span>}
  </div>
);

const exportMyCandidatesCSV = (data) => {
  const headers = [
    "Candidate Name",
    "Email",
    "Contact Number",
    "Qualification",
    "Qualification Type",
    "Skills",
    "Experience",
    "Expected Salary",
    "Source",
    "Applied Jobs",
  ];

  const escape = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const rows = data.map((c) =>
    [
      c.candidateName || "",
      c.email || "",
      c.contactNumber || "",
      c.qualification || "",
      c.qualificationType || "",
      c.skills || "",
      c.experience || "",
      c.expectedSalary || "",
      c.sourceFrom || "",
      c.jobTitle ||
        (c.jobs || [])
          .map((j) => j.jobTitle)
          .filter(Boolean)
          .join(" | ") ||
        (c.appliedJobs || [])
          .map((j) => j.jobTitle)
          .filter(Boolean)
          .join(" | "),
    ]
      .map(escape)
      .join(","),
  );

  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `my-candidates-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const getInitials = (name) => {
  const safeName = String(name || "");
  return (
    safeName
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?"
  );
};

const avatarColor = (name) => {
  const safeName = String(name || "");
  const colors = [
    "#6366f1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  return colors[(safeName.charCodeAt(0) || 0) % colors.length];
};

const scoreCandidate = (c, filters) => {
  let score = 0;
  if (filters.skills) {
    const wanted = filters.skills
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const has = (c.skills || "").toLowerCase();
    wanted.forEach((s) => {
      if (has.includes(s)) score += 10;
    });
  }
  if (filters.salaryMax && c.expectedSalary) {
    const max = Number(filters.salaryMax);
    const sal = Number(c.expectedSalary);
    if (sal <= max) score += 20;
    else if (sal <= max * 1.1) score += 10;
  }
  if (filters.qualificationType?.length && c.qualificationType) {
    if (
      filters.qualificationType
        .map((q) => q.toUpperCase())
        .includes(c.qualificationType.toUpperCase())
    )
      score += 15;
  }
  if (filters.qualification?.length && c.qualification) {
    if (
      filters.qualification
        .map((q) => q.toLowerCase())
        .includes(c.qualification.toLowerCase())
    )
      score += 10;
  }
  return score;
};

// ─── TablePagination ──────────────────────────────────────────────────────────
const TablePagination = ({ page, total, pageSize, onChange }) => {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 2) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }
  return (
    <div className="jp-pagination">
      <span className="jp-pagination__info">
        {from}–{to} of {total}
      </span>
      <div className="jp-pagination__btns">
        <button
          className="jp-pagination__btn"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
        >
          ‹ Prev
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e-${i}`} className="jp-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`jp-pagination__btn${p === page ? " jp-pagination__btn--active" : ""}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className="jp-pagination__btn"
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next ›
        </button>
      </div>
    </div>
  );
};

const normalizeInterviewStatus = (status = "") =>
  String(status).trim().toUpperCase();

// isSchedulable — a candidate can be scheduled only if no interview is
// currently scheduled for them AND they haven't reached a terminal
// interviewStatus (REJECTED / SELECTED). Prevents scheduling rejected or
// already-selected candidates just because their scheduledInterview flag
// happens to be false (e.g. right after a round is rejected/cancelled).
//
// NOTE: `interviewStatus` (SCHEDULED/PROCESSING/REJECTED/SELECTED/...) is a
// per-round status and is a DIFFERENT field from the application-level
// `status`/`applicationStatus` (APPLIED/MOVED/REJECTED) returned by the
// applied-candidates APIs. A candidate whose *application* was rejected can
// still carry a stale/non-terminal `interviewStatus` such as
// "NOT_SCHEDULED" or "PROCESSING" (e.g. they were rejected before any round
// was ever scheduled), so checking `interviewStatus` alone is not enough —
// we must also block scheduling once the application itself is REJECTED.
const TERMINAL_INTERVIEW_STATUSES = ["REJECTED", "SELECTED"];
const isSchedulable = (c = {}) =>
  !c.scheduledInterview &&
  !!c.jobPostId &&
  normalizeInterviewStatus(c.applicationStatus ?? c.status) !== "REJECTED" &&
  !TERMINAL_INTERVIEW_STATUSES.includes(
    normalizeInterviewStatus(c.interviewStatus),
  );

// ─────────────────────────────────────────────────────────────────────────────
//  AllCandidates — workspace router
// ─────────────────────────────────────────────────────────────────────────────
const AllCandidates = () => {
  const { isConsultant } = useWorkspace();
  return isConsultant ? <ConsultantCandidatePage /> : <CompanyCandidatePage />;
};

// ═════════════════════════════════════════════════════════════════════════════
//  COMPANY
// ═════════════════════════════════════════════════════════════════════════════
// ─── PATCH: Add to CompanyCandidatePage ──────────────────────────────────────
// Replace the existing CompanyCandidatePage and add the new ManageSlotsPage component.
// Everything else in Candidates.jsx stays the same.

// ═════════════════════════════════════════════════════════════════════════════
//  COMPANY — updated with Manage Slots sub-tab inside Schedule Slot
// ═════════════════════════════════════════════════════════════════════════════
const CompanyCandidatePage = () => {
  const { hasRole } = useAuth();
  const isRecruiterHR = hasRole("ROLE_RECRUITER-HR");
  const [tab, setTab] = useState(isRecruiterHR ? "pool" : "schedule");
  // sub-tab inside "schedule"
  const [scheduleSubTab, setScheduleSubTab] = useState("my");

  useEffect(() => {
    if (!isRecruiterHR && tab !== "schedule") {
      setTab("schedule");
    }
  }, [isRecruiterHR]); // eslint-disable-line

  return (
    <div className="cand-page">
      <div className="cand-tab-bar">
        {/* Schedule Slot — visible to ALL roles */}
        <button
          className={`cand-tab${tab === "schedule" ? " cand-tab--active" : ""}`}
          onClick={() => setTab("schedule")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect
              x="1"
              y="2"
              width="12"
              height="11"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M4 1v2M10 1v2M1 5.5h12"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
            <circle cx="7" cy="9" r="1.5" fill="currentColor" />
          </svg>
          Schedule Slot
        </button>

        {/* Applied Candidates — ROLE_RECRUITER-HR only */}
        {isRecruiterHR && (
          <button
            className={`cand-tab${tab === "pool" ? " cand-tab--active" : ""}`}
            onClick={() => setTab("pool")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="5"
                cy="4.5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M1 12c0-2.21 1.79-4 4-4s4 1.79 4 4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
              <circle
                cx="10.5"
                cy="4.5"
                r="2"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M10.5 8.5c1.38 0 2.5 1.12 2.5 2.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            Applied Candidate
          </button>
        )}

        {/* Consultancy Candidates — ROLE_RECRUITER-HR only */}
        {isRecruiterHR && (
          <button
            className={`cand-tab${tab === "consultancy" ? " cand-tab--active" : ""}`}
            onClick={() => setTab("consultancy")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="3"
                width="12"
                height="9"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M5 3V2a2 2 0 014 0v1"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path d="M1 7h12" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            Consultancy Candidates
          </button>
        )}

        {/* All Candidates — ROLE_RECRUITER-HR only */}
        {isRecruiterHR && (
          <button
            className={`cand-tab${tab === "all" ? " cand-tab--active" : ""}`}
            onClick={() => setTab("all")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="5"
                cy="4.5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M1 12c0-2.21 1.79-4 4-4s4 1.79 4 4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
              <circle
                cx="10.5"
                cy="4.5"
                r="2"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M10.5 8.5c1.38 0 2.5 1.12 2.5 2.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            All Candidates
          </button>
        )}
      </div>

      {tab === "pool" && isRecruiterHR && <CandidatePool />}
      {tab === "consultancy" && isRecruiterHR && <ConsultancyCandidates />}
      {tab === "all" && isRecruiterHR && <AllCandidate />}

      {tab === "schedule" && (
        <div>
          {/* ── Sub-tab bar inside Schedule Slot ─────────────────────── */}
          <div className="cand-subtab-bar">
            <button
              className={`cand-subtab${scheduleSubTab === "my" ? " cand-subtab--active" : ""}`}
              onClick={() => setScheduleSubTab("my")}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <circle
                  cx="6.5"
                  cy="4"
                  r="2.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                />
                <path
                  d="M1.5 12c0-2.76 2.24-5 5-5s5 2.24 5 5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              My Interviews
            </button>

            {isRecruiterHR && (
              <button
                className={`cand-subtab${scheduleSubTab === "manage" ? " cand-subtab--active" : ""}`}
                onClick={() => setScheduleSubTab("manage")}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <rect
                    x="1"
                    y="1.5"
                    width="11"
                    height="10"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <path
                    d="M4 1.5v1.5M9 1.5v1.5M1 5h11"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                  <path
                    d="M4 7.5h5M4 9.5h3"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
                Manage Slots
              </button>
            )}
          </div>

          {scheduleSubTab === "my" && <ScheduleInterviewPage />}
          {scheduleSubTab === "manage" && isRecruiterHR && <ManageSlotsPage />}
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  ManageSlotsPage — fetches /recruiter-hr/candidate/job-posts/interview-rounds-slots
// ═════════════════════════════════════════════════════════════════════════════
const MANAGE_SLOTS_API = `${process.env.REACT_APP_API_HR_URL}/candidate/job-posts/interview-rounds-slots`;

const SLOT_STATUS_COLORS = {
  AVAILABLE: { bg: "#dcfce7", color: "#15803d", label: "Available" },
  BOOKED: { bg: "#dbeafe", color: "#1d4ed8", label: "Booked" },
  CANCELLED: { bg: "#fee2e2", color: "#b91c1c", label: "Cancelled" },
  COMPLETED: { bg: "#f3f4f6", color: "#374151", label: "Completed" },
  PENDING: { bg: "#fef9c3", color: "#854d0e", label: "Pending" },
};

const SlotStatusBadge = ({ status }) => {
  const s = SLOT_STATUS_COLORS[(status || "").toUpperCase()] || {
    bg: "#f3f4f6",
    color: "#6b7280",
    label: status || "—",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 9px",
        borderRadius: 20,
        fontSize: "0.75rem",
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.color,
          flexShrink: 0,
        }}
      />
      {s.label}
    </span>
  );
};

const formatSlotDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDuration = (dur) => {
  if (!dur) return "—";
  // dur.seconds is total seconds
  const totalSec = Number(dur.seconds || 0);
  if (!totalSec) return "—";
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

const ManageSlotsPage = () => {
  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [searchSlot, setSearchSlot] = useState("");
  const [filterJobPostId, setFilterJobPostId] = useState("all");
  const [filterRoundId, setFilterRoundId] = useState("all");
  const [filterInterviewer, setFilterInterviewer] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMode, setFilterMode] = useState("all");
  const [slotPage, setSlotPage] = useState(1);
  const SLOT_PAGE_SIZE = 20;

  useEffect(() => {
    setLoading(true);
    axios
      .get(MANAGE_SLOTS_API)
      .then((res) => setJobPosts(res.data.data || []))
      .catch((err) =>
        setError(err?.response?.data?.message || "Failed to load slots."),
      )
      .finally(() => setLoading(false));
  }, []);

  // Flatten all slots into rows
  const allRows = useMemo(() => {
    const rows = [];
    jobPosts.forEach((jp) => {
      (jp.interviewRounds || []).forEach((round) => {
        (round.interviewers || []).forEach((interviewer) => {
          (interviewer.slots || []).forEach((slot) => {
            rows.push({
              jobPostId: jp.jobPostId,
              jobTitle: jp.jobTitle || "—",
              roundId: round.roundId,
              roundName: round.roundName || "—",
              interviewMode: round.interviewMode || "—",
              empId: interviewer.empId,
              interviewerName: interviewer.interviewerName || "—",
              slotId: slot.slotId,
              startTime: slot.startTime,
              endTime: slot.endTime,
              slotStatus: slot.slotStatus || "—",
              comment: slot.comment || "",
              duration: slot.duration,
              slotHistories: slot.slotHistories || [],
            });
          });
        });
      });
    });
    return rows;
  }, [jobPosts]);

  // Dropdown options
  const jobOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!map.has(r.jobPostId)) map.set(r.jobPostId, r.jobTitle);
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [allRows]);

  const roundOptions = useMemo(() => {
    const map = new Map();
    allRows
      .filter(
        (r) =>
          filterJobPostId === "all" || r.jobPostId === Number(filterJobPostId),
      )
      .forEach((r) => {
        if (!map.has(r.roundId)) map.set(r.roundId, r.roundName);
      });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [allRows, filterJobPostId]);

  const interviewerOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!map.has(r.empId)) map.set(r.empId, r.interviewerName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [allRows]);

  const statusOptions = useMemo(() => {
    const set = new Set(
      allRows.map((r) => (r.slotStatus || "").toUpperCase()).filter(Boolean),
    );
    return Array.from(set);
  }, [allRows]);

  const modeOptions = useMemo(() => {
    const set = new Set(
      allRows.map((r) => (r.interviewMode || "").toUpperCase()).filter(Boolean),
    );
    return Array.from(set);
  }, [allRows]);

  // Reset page on filter change
  useEffect(() => {
    setSlotPage(1);
  }, [
    searchSlot,
    filterJobPostId,
    filterRoundId,
    filterInterviewer,
    filterStatus,
    filterMode,
  ]);

  // Also reset round filter when job changes
  useEffect(() => {
    setFilterRoundId("all");
  }, [filterJobPostId]);

  const filteredRows = useMemo(() => {
    const q = searchSlot.toLowerCase();
    return allRows.filter((r) => {
      const matchSearch =
        !q ||
        r.jobTitle.toLowerCase().includes(q) ||
        r.roundName.toLowerCase().includes(q) ||
        r.interviewerName.toLowerCase().includes(q) ||
        (r.comment || "").toLowerCase().includes(q);
      const matchJob =
        filterJobPostId === "all" || r.jobPostId === Number(filterJobPostId);
      const matchRound =
        filterRoundId === "all" || r.roundId === Number(filterRoundId);
      const matchInterviewer =
        filterInterviewer === "all" || r.empId === Number(filterInterviewer);
      const matchStatus =
        filterStatus === "all" ||
        (r.slotStatus || "").toUpperCase() === filterStatus.toUpperCase();
      const matchMode =
        filterMode === "all" ||
        (r.interviewMode || "").toUpperCase() === filterMode.toUpperCase();

      return (
        matchSearch &&
        matchJob &&
        matchRound &&
        matchInterviewer &&
        matchStatus &&
        matchMode
      );
    });
  }, [
    allRows,
    searchSlot,
    filterJobPostId,
    filterRoundId,
    filterInterviewer,
    filterStatus,
    filterMode,
  ]);

  const totalPages = Math.ceil(filteredRows.length / SLOT_PAGE_SIZE);
  const pagedRows = filteredRows.slice(
    (slotPage - 1) * SLOT_PAGE_SIZE,
    slotPage * SLOT_PAGE_SIZE,
  );

  // Summary stats
  const stats = useMemo(() => {
    const total = allRows.length;
    const available = allRows.filter(
      (r) => (r.slotStatus || "").toUpperCase() === "AVAILABLE",
    ).length;
    const booked = allRows.filter(
      (r) => (r.slotStatus || "").toUpperCase() === "BOOKED",
    ).length;
    const cancelled = allRows.filter(
      (r) => (r.slotStatus || "").toUpperCase() === "CANCELLED",
    ).length;
    return [
      {
        label: "Total Slots",
        value: total,
        tone: "blue",
        sub: `${jobOptions.length} job posts`,
      },
      {
        label: "Available",
        value: available,
        tone: "green",
        sub: "Open for booking",
      },
      {
        label: "Booked",
        value: booked,
        tone: "amber",
        sub: "Interviews scheduled",
      },
      {
        label: "Cancelled",
        value: cancelled,
        tone: "red",
        sub: "Not available",
      },
    ];
  }, [allRows, jobOptions.length]);

  const clearFilters = () => {
    setSearchSlot("");
    setFilterJobPostId("all");
    setFilterRoundId("all");
    setFilterInterviewer("all");
    setFilterStatus("all");
    setFilterMode("all");
  };

  const hasActiveFilters =
    searchSlot ||
    filterJobPostId !== "all" ||
    filterRoundId !== "all" ||
    filterInterviewer !== "all" ||
    filterStatus !== "all" ||
    filterMode !== "all";

  return (
    <>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="cand-page__header" style={{ marginBottom: 16 }}>
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Manage Interview Slots</h2>
            <span className="cand-page__count">
              {filteredRows.length} slots
            </span>
          </div>
          <p className="cand-page__sub">
            View and manage all interview slots across job posts, rounds, and
            interviewers.
          </p>
        </div>
        {hasActiveFilters && (
          <button
            className="cand-filter-clear"
            onClick={clearFilters}
            style={{ alignSelf: "center" }}
          >
            ✕ Clear all filters
          </button>
        )}
      </div>

      {/* ── Stats row ─────────────────────────────────────────────────── */}
      {!loading && (
        <div className="cand-stats-row" style={{ marginBottom: 16 }}>
          {stats.map((s) => (
            <article key={s.label} className={`cand-stat cand-stat--${s.tone}`}>
              <span className="cand-stat__bar" />
              <div className="cand-stat__body">
                <div className="cand-stat__text">
                  <p className="cand-stat__label">{s.label}</p>
                  <p className="cand-stat__sub">{s.sub}</p>
                </div>
                <h3 className="cand-stat__value">{s.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
          alignItems: "center",
          padding: "12px 14px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
        }}
      >
        {/* Search */}
        <div
          className="cand-search-wrap"
          style={{ minWidth: 200, flex: "1 1 200px" }}
        >
          <svg
            className="cand-search-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle
              cx="6.5"
              cy="6.5"
              r="4.5"
              stroke="#9ca3af"
              strokeWidth="1.5"
            />
            <path
              d="M10.5 10.5l3 3"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            className="cand-search"
            placeholder="Search job, round, interviewer..."
            value={searchSlot}
            onChange={(e) => setSearchSlot(e.target.value)}
          />
          {searchSlot && (
            <button
              className="cand-search-clear"
              onClick={() => setSearchSlot("")}
            >
              ×
            </button>
          )}
        </div>

        {/* Job Post */}
        <select
          className="cand-input"
          style={{ minWidth: 160, flex: "1 1 160px" }}
          value={filterJobPostId}
          onChange={(e) => setFilterJobPostId(e.target.value)}
        >
          <option value="all">All Job Posts</option>
          {jobOptions.map((j) => (
            <option key={j.id} value={j.id}>
              {j.title}
            </option>
          ))}
        </select>

        {/* Round */}
        <select
          className="cand-input"
          style={{ minWidth: 150, flex: "1 1 150px" }}
          value={filterRoundId}
          onChange={(e) => setFilterRoundId(e.target.value)}
          disabled={roundOptions.length === 0}
        >
          <option value="all">All Rounds</option>
          {roundOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        {/* Interviewer */}
        <select
          className="cand-input"
          style={{ minWidth: 160, flex: "1 1 160px" }}
          value={filterInterviewer}
          onChange={(e) => setFilterInterviewer(e.target.value)}
        >
          <option value="all">All Interviewers</option>
          {interviewerOptions.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>

        {/* Mode */}
        <select
          className="cand-input"
          style={{ minWidth: 130, flex: "0 1 130px" }}
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
        >
          <option value="all">All Modes</option>
          {modeOptions.map((m) => (
            <option key={m} value={m}>
              {m.charAt(0) + m.slice(1).toLowerCase()}
            </option>
          ))}
        </select>

        {/* Status pills */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {["all", ...statusOptions].map((s) => (
            <button
              key={s}
              className={`cand-job-pill${filterStatus === s ? " cand-job-pill--active" : ""}`}
              onClick={() => setFilterStatus(s)}
              style={{ fontSize: "0.75rem", padding: "3px 10px" }}
            >
              {s === "all"
                ? "All Statuses"
                : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="cand-loader">Loading interview slots...</div>
      ) : error ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">⚠️</span>
          <p>{error}</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">📅</span>
          <p>
            {hasActiveFilters
              ? "No slots match your filters."
              : "No interview slots found."}
          </p>
          {hasActiveFilters && (
            <button
              className="cand-job-pill"
              style={{ marginTop: 8 }}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="cand-table-wrapper">
          <table className="cand-table">
            <thead>
              <tr>
                <th style={{ width: 48, textAlign: "center" }}>SL No</th>
                <th>Job Post</th>
                <th>Round</th>
                <th>Mode</th>
                <th>Interviewer</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Comment</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, idx) => (
                <tr key={`${row.slotId}-${idx}`}>
                  <td className="cand-td-num" style={{ textAlign: "center" }}>
                    {(slotPage - 1) * SLOT_PAGE_SIZE + idx + 1}
                  </td>
                  <td>
                    <div>
                      <div
                        className="cand-name"
                        style={{ fontSize: "0.825rem" }}
                      >
                        {row.jobTitle}
                      </div>
                      <div className="cand-email">#{row.jobPostId}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontSize: "0.825rem", fontWeight: 500 }}>
                        {row.roundName}
                      </div>
                      <div className="cand-email">#{row.roundId}</div>
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 20,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        background:
                          (row.interviewMode || "").toUpperCase() === "ONLINE"
                            ? "#eff6ff"
                            : (row.interviewMode || "").toUpperCase() ===
                                "OFFLINE"
                              ? "#f0fdf4"
                              : "#fef3c7",
                        color:
                          (row.interviewMode || "").toUpperCase() === "ONLINE"
                            ? "#1d4ed8"
                            : (row.interviewMode || "").toUpperCase() ===
                                "OFFLINE"
                              ? "#15803d"
                              : "#92400e",
                      }}
                    >
                      {row.interviewMode}
                    </span>
                  </td>
                  <td>
                    <div className="cand-identity">
                      <div
                        className="cand-avatar"
                        style={{
                          background: avatarColor(row.interviewerName),
                          width: 28,
                          height: 28,
                          fontSize: "0.65rem",
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(row.interviewerName)}
                      </div>
                      <div>
                        <div style={{ fontSize: "0.825rem", fontWeight: 500 }}>
                          {row.interviewerName}
                        </div>
                        <div className="cand-email">ID #{row.empId}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    {formatSlotDateTime(row.startTime)}
                  </td>
                  <td style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    {formatSlotDateTime(row.endTime)}
                  </td>
                  <td style={{ fontSize: "0.8rem", textAlign: "center" }}>
                    {formatDuration(row.duration)}
                  </td>
                  <td>
                    <SlotStatusBadge status={row.slotStatus} />
                  </td>
                  <td
                    style={{
                      fontSize: "0.8rem",
                      maxWidth: 160,
                      color: "#64748b",
                    }}
                  >
                    {row.comment || <span className="cand-na">—</span>}
                  </td>
                  <td>
                    {row.slotHistories.length > 0 ? (
                      <SlotHistoryTooltip histories={row.slotHistories} />
                    ) : (
                      <span className="cand-na">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <TablePagination
              page={slotPage}
              total={filteredRows.length}
              pageSize={SLOT_PAGE_SIZE}
              onChange={setSlotPage}
            />
          )}
        </div>
      )}
    </>
  );
};

// ─── SlotHistoryTooltip ───────────────────────────────────────────────────────
const SlotHistoryTooltip = ({ histories }) => (
  <div className="cand-jobs-compact">
    <span className="cand-jobs-compact__count">{histories.length}</span>
    <span className="cand-jobs-compact__label">
      {histories.length === 1 ? "entry" : "entries"}
    </span>
    <div className="cand-jobs-compact__tooltip" style={{ minWidth: 220 }}>
      {histories.map((h) => (
        <div
          key={h.historyId}
          className="cand-jobs-compact__row"
          style={{ flexDirection: "column", gap: 2, alignItems: "flex-start" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              gap: 6,
            }}
          >
            <span
              className="cand-jobs-compact__title"
              style={{ fontWeight: 600 }}
            >
              {h.historyStatus || "—"}
            </span>
            <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
              #{h.historyId}
            </span>
          </div>
          {h.scheduledBy && (
            <span style={{ fontSize: "0.72rem", color: "#64748b" }}>
              by {h.scheduledBy}
            </span>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── CandidatePool ────────────────────────────────────────────────────────────
const CandidatePool = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default"); // ← NEW

  const [timelineTarget, setTimelineTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [flows, setFlows] = useState([]);

  const location = useLocation();
  const [selectedJobPostId, setSelectedJobPostId] = useState(
    location.state?.jobPostId || null,
  );
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [tablePage, setTablePage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const TABLE_PAGE_SIZE = 50;

  const STATUS_FILTERS = [
    { key: "ALL", label: "All Statuses" },
    { key: "SHORTLISTED", label: "Shortlisted" },
    { key: "PROCESSING", label: "Processing" },
    { key: "HOLD", label: "Hold" },
    { key: "REJECTED", label: "Rejected" },
    { key: "SELECTED", label: "Selected" },
    { key: "SCHEDULED", label: "Scheduled" },
  ];

  const [filters, setFilters] = useState({
    skills: "",
    salaryMax: "",
    qualificationType: [],
    qualification: [],
  });

  const toggleSelectAll = () => {
    const pageIds = filtered
      .slice((tablePage - 1) * TABLE_PAGE_SIZE, tablePage * TABLE_PAGE_SIZE)
      .filter(isSchedulable)
      .map((c) => c.appliedCandidateId);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setTablePage(1);
    setSelectedIds(new Set());
  }, [statusFilter, selectedJobPostId, search, filters, sortBy]);

  const hasActiveFilters =
    !!filters.skills ||
    !!filters.salaryMax ||
    filters.qualificationType.length > 0 ||
    filters.qualification.length > 0;

  const navigate = useNavigate();

  const fetchCandidates = useCallback(() => {
    setLoading(true);
    axios
      .get(APPLIED_API)
      .then((res) => {
        const flat = res.data.data || [];
        const rows = flat.map((item) => {
          // The API returns candidateName / email / phone inside interviewHistories[],
          // not at the top level of the item. Use the most-recent history entry as
          // the fallback source so these fields (and therefore A→Z / Z→A sorting)
          // always resolve to real values.
          const latestHistory =
            (item.interviewHistories ?? []).slice(-1)[0] ?? {};

          const candidateName =
            item.candidateName ??
            item.candidate?.name ??
            latestHistory.candidateName ??
            "";

          const email =
            item.email ??
            item.candidate?.email ??
            latestHistory.candidateEmail ??
            "";

          const contactNumber =
            item.candidate?.contactNumber ??
            latestHistory.candidatePhone ??
            null;

          // `appliedOn` is a top-level field per the API schema; keep the
          // existing fallback chain so date-based sorting always has a value.
          const appliedOn = item.appliedOn ?? item.createdAt ?? null;

          return {
            recuirtmentCandidateId:
              item.candidate?.candidateId ??
              item.candidate?.id ??
              item.appliedCandidateId,
            candidateId:
              item.candidate?.candidateId ?? item.candidate?.id ?? null,
            appliedCandidateId: item.appliedCandidateId,
            candidateName,
            resumeUrl: item.candidate?.resumeUrl ?? null,
            portfolioLink: item.candidate?.portfolioLink ?? null,
            idProof: item.candidate?.idProof ?? null,
            email,
            contactNumber,
            qualification: item.candidate?.qualification ?? null,
            qualificationType: item.candidate?.qualificationType ?? null,
            skills: item.candidate?.skills ?? null,
            experience: item.candidate?.experience ?? null,
            expectedSalary: item.candidate?.expectedSalary ?? null,
            sourceFrom: item.sources?.[0]?.sourceName ?? null,
            jobPostId: item.jobPostId ?? null,
            jobTitle: item.jobTitle ?? "",
            jobPostCode: item.jobPostCode ?? null,
            interviewStatus: item.interviewStatus ?? null,
            interviewRoundId: item.interviewRoundId ?? null,
            interviewRoundName: item.interviewRoundName ?? null,
            interviewHistoryId: item.interviewHistoryId ?? null,
            appliedCandidateHistoryId: item.appliedCandidateId ?? null,
            hiringFlowId: item.hiringFlowId ?? null,
            interviewRounds: item.interviewRounds ?? [],
            interviewHistories: item.interviewHistories ?? [],
            recentSlotUrl:
              latestHistory.url ?? item.url ?? item.slotUrl ?? null,
            interviewDateTime: item.interviewDateTime ?? null,
            interviewerName: item.interviewerName ?? null,
            roundMode: item.roundMode ?? null,
            sourceId: item.sources?.[0]?.id ?? null,
            sourceName: item.sources?.[0]?.sourceName ?? null,
            scheduledInterview: item.scheduledInterview ?? false,
            sources: item.sources ?? [],
            appliedOn,
            // ── Additional fields from /candidate/applied/jobpost/all ──
            applicationStatus: item.status ?? null,
            candidateStatus: item.candidate?.status ?? null,
            candidateMessage: item.candidate?.message ?? null,
            expectedClosingDate: item.expectedClosingDate ?? null,
            expectedJoiningDate: item.expectedJoiningDate ?? null,
            applicantJoiningStatus: item.applicantJoiningStatus ?? null,
            noticePeriodDays: item.noticePeriodDays ?? null,
            currentLocation: item.currentLocation ?? null,
            currentCompany: item.currentCompany ?? null,
            preferredWorkLocation: item.preferredWorkLocation ?? null,
            currentCompanyDesignation: item.currentCompanyDesignation ?? null,
          };
        });
        setCandidates(rows);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_HR_URL}/hiring-flow`)
      .then((r) => setFlows(r.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.jobPostId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const allJobPosts = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      if (c.jobPostId && !map.has(c.jobPostId)) {
        map.set(c.jobPostId, {
          jobPostId: c.jobPostId,
          jobTitle: c.jobTitle,
          jobPostCode: c.jobPostCode,
        });
      }
    });
    return Array.from(map.values());
  }, [candidates]);

  const filtered = useMemo(() => {
    let result = candidates.filter((c) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        c.candidateName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.skills?.toLowerCase().includes(q) ||
        c.qualification?.toLowerCase().includes(q);

      const matchJob = !selectedJobPostId || c.jobPostId === selectedJobPostId;

      const matchSalary =
        !filters.salaryMax ||
        !c.expectedSalary ||
        Number(c.expectedSalary) <= Number(filters.salaryMax) * 1.1;

      const matchQualType =
        filters.qualificationType.length === 0 ||
        filters.qualificationType
          .map((q) => q.toUpperCase())
          .includes((c.qualificationType || "").toUpperCase());

      const matchQual =
        filters.qualification.length === 0 ||
        filters.qualification
          .map((q) => q.toLowerCase())
          .includes((c.qualification || "").toLowerCase());

      const matchSkills = (() => {
        if (!filters.skills) return true;
        const wanted = filters.skills
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        const has = (c.skills || "").toLowerCase();
        return wanted.some((s) => has.includes(s));
      })();

      const matchStatus =
        statusFilter === "ALL" ||
        (c.interviewStatus || "").toUpperCase() === statusFilter;

      return (
        matchSearch &&
        matchJob &&
        matchSalary &&
        matchQualType &&
        matchQual &&
        matchSkills &&
        matchStatus
      );
    });

    // Rank by score when smart filters active, otherwise apply manual sort
    if (hasActiveFilters && sortBy === "default") {
      result = result
        .map((c) => ({ ...c, _score: scoreCandidate(c, filters) }))
        .sort((a, b) => b._score - a._score);
    } else {
      result = applySorting(result, sortBy);
    }

    return result;
  }, [
    candidates,
    search,
    selectedJobPostId,
    filters,
    hasActiveFilters,
    statusFilter,
    sortBy,
  ]);

  const selectedCandidates = filtered.filter((c) =>
    selectedIds.has(c.appliedCandidateId),
  );

  const handleForceDeleteConfirm = async () => {
    if (!forceDeleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${COMPANY_API}/force/${forceDeleteTarget.recuirtmentCandidateId}`,
      );
      toast.success(res?.data?.message || "Candidate force deleted.");
      setForceDeleteTarget(null);
      fetchCandidates();
    } catch {
      toast.error("Force delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = async (target) => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${COMPANY_API}/${target.recuirtmentCandidateId}`);
      toast.success("Candidate deleted.");
      fetchCandidates();
    } catch (err) {
      if (err?.response?.status === 409) {
        setForceDeleteTarget({
          ...target,
          _conflictMessage: err?.response?.data?.message,
        });
      } else {
        toast.error(err?.response?.data?.message || "Failed to delete.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const adaptForPipeline = (c) => ({
    ...c,
    candidate: { name: c.candidateName, email: c.email },
    jobPostName: c.jobTitle ?? null,
  });

  const candidateStats = useMemo(() => {
    const uniqueCandidates = new Set(
      candidates
        .map((c) => c.recuirtmentCandidateId || c.candidateId)
        .filter(Boolean),
    ).size;

    const shortlistedCount = filtered.filter((c) =>
      ["SHORTLISTED", "SELECTED"].includes(
        normalizeInterviewStatus(c.interviewStatus),
      ),
    ).length;

    const scheduledCount = filtered.filter(
      (c) =>
        c.scheduledInterview ||
        normalizeInterviewStatus(c.interviewStatus) === "SCHEDULED",
    ).length;

    return [
      {
        label: "Applications",
        value: filtered.length,
        tone: "blue",
        sub: `${candidates.length} total records`,
      },
      {
        label: "Unique Candidates",
        value: uniqueCandidates,
        tone: "green",
        sub: "Distinct people in pipeline",
      },
      {
        label: "Shortlisted",
        value: shortlistedCount,
        tone: "amber",
        sub: "Shortlisted or selected",
      },
      {
        label: "Scheduled",
        value: scheduledCount,
        tone: "violet",
        sub: `${allJobPosts.length} active job posts`,
      },
    ];
  }, [allJobPosts.length, candidates, filtered]);

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Applied Candidates</h2>
            <span className="cand-page__count">
              {filtered.length} candidates
            </span>
          </div>
          <p className="cand-page__sub">
            Track every application, interview stage, and candidate progress in
            one workspace.
          </p>
        </div>
        <div className="cand-page__header-actions">
          <button
            className="cand-top-action-btn"
            onClick={() => exportMyCandidatesCSV(filtered)}
            disabled={filtered.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>
          <button
            className="cand-top-action-btn"
            onClick={() => setBulkUploadOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1v9M5 4l3-3 3 3M1 11v1.5A1.5 1.5 0 0 0 2.5 14h11A1.5 1.5 0 0 0 15 12.5V11"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Bulk Upload
          </button>
          <button
            className="cand-create-btn"
            onClick={() => {
              setEditTarget(null);
              setDrawerOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Add Candidate
          </button>
        </div>
      </div>

      {!loading && (
        <div className="cand-stats-row" style={{ marginBottom: "10px" }}>
          {candidateStats.map((stat) => (
            <article
              key={stat.label}
              className={`cand-stat cand-stat--${stat.tone}`}
            >
              <span className="cand-stat__bar" />
              <div className="cand-stat__body">
                <div className="cand-stat__text">
                  <p className="cand-stat__label">{stat.label}</p>
                  <p className="cand-stat__sub">{stat.sub}</p>
                </div>
                <h3 className="cand-stat__value">{stat.value}</h3>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && allJobPosts.length > 0 && (
        <div className="cand-job-filter-bar">
          <button
            className={`cand-job-pill${!selectedJobPostId ? " cand-job-pill--active" : ""}`}
            onClick={() => setSelectedJobPostId(null)}
          >
            All Applied Candidates
          </button>
          {allJobPosts.map((j) => (
            <button
              key={j.jobPostId}
              className={`cand-job-pill${selectedJobPostId === j.jobPostId ? " cand-job-pill--active" : ""}`}
              onClick={() =>
                setSelectedJobPostId(
                  selectedJobPostId === j.jobPostId ? null : j.jobPostId,
                )
              }
            >
              {j.jobTitle}
              {j.jobPostCode && (
                <span
                  style={{
                    opacity: 0.6,
                    marginLeft: 4,
                    fontSize: "0.75em",
                  }}
                >
                  #{j.jobPostCode}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="cand-toolbar">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <FilterDropdown
            hasActiveFilters={hasActiveFilters}
            onClear={() =>
              setFilters({
                skills: "",
                salaryMax: "",
                qualificationType: [],
                qualification: [],
              })
            }
          >
            <div className="cand-filter-panel__grid">
              <F label="Skills (comma-separated)">
                <input
                  className="cand-input"
                  placeholder="e.g. Communication, Sales, Excel"
                  value={filters.skills}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, skills: e.target.value }))
                  }
                />
              </F>
              <F label="Max Expected Salary (₹)">
                <input
                  className="cand-input"
                  type="number"
                  placeholder="e.g. 800000"
                  value={filters.salaryMax}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, salaryMax: e.target.value }))
                  }
                />
              </F>
              <F label="Qualification Type">
                <MultiSelectDropdown
                  label="Qualification Type"
                  options={QUALIFICATION_TYPES}
                  selected={filters.qualificationType}
                  onChange={(val) =>
                    setFilters((p) => ({
                      ...p,
                      qualificationType: val,
                      qualification: [],
                    }))
                  }
                  placeholder="Any type"
                />
              </F>
              <F label="Qualification">
                <MultiSelectDropdown
                  label="Qualification"
                  options={
                    filters.qualificationType.length === 1
                      ? QUALIFICATION_OPTIONS[filters.qualificationType[0]] ||
                        []
                      : filters.qualificationType.length > 1
                        ? filters.qualificationType.flatMap(
                            (qt) => QUALIFICATION_OPTIONS[qt] || [],
                          )
                        : []
                  }
                  selected={filters.qualification}
                  onChange={(val) =>
                    setFilters((p) => ({ ...p, qualification: val }))
                  }
                  placeholder={
                    filters.qualificationType.length === 0
                      ? "Select type first"
                      : "Any qualification"
                  }
                />
              </F>
            </div>
            {hasActiveFilters && (
              <p className="cand-filter-panel__hint">
                ✦ Candidates ranked by match score.{" "}
                {sortBy !== "default" && "Manual sort overrides score ranking."}
              </p>
            )}
          </FilterDropdown>

          <SortBar sortBy={sortBy} setSortBy={setSortBy} />
        </div>

        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.key}
                className={`cand-job-pill cand-job-pill--status${statusFilter === s.key ? " cand-job-pill--active" : ""}${s.key !== "ALL" ? ` cand-job-pill--${s.key.toLowerCase()}` : ""}`}
                onClick={() => setStatusFilter(s.key)}
              >
                {s.key !== "ALL" && <span className="cand-status-dot" />}
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <SearchBar search={search} setSearch={setSearch} />
      </div>

      {selectedIds.size > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <rect
              x="1"
              y="2"
              width="13"
              height="11"
              rx="1.5"
              stroke="#2563eb"
              strokeWidth="1.4"
            />
            <path
              d="M4 1v2M11 1v2M1 6h13"
              stroke="#2563eb"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
            <path
              d="M5 9.5l1.8 1.8L10 7"
              stroke="#2563eb"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{ fontWeight: 600, color: "#1e40af", fontSize: "0.875rem" }}
          >
            {selectedIds.size} candidate{selectedIds.size !== 1 ? "s" : ""}{" "}
            selected
          </span>
          <div style={{ flex: 1 }} />
          <button
            className="cand-create-btn"
            style={{ fontSize: "0.8rem", padding: "6px 14px" }}
            onClick={() => setBulkScheduleOpen(true)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="2"
                width="12"
                height="11"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M4 1v2M10 1v2M1 5.5h12"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
              <path
                d="M7 8v2M6 9h2"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            Schedule Interviews
          </button>
          <button
            className="cand-filter-clear"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <div className="cand-loader">Loading candidates...</div>
      ) : filtered.length === 0 ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">👤</span>
          <p>
            {search || selectedJobPostId || statusFilter !== "ALL"
              ? "No candidates match your filter."
              : "No candidates added yet."}
          </p>
          {(selectedJobPostId || statusFilter !== "ALL") && (
            <button
              className="cand-job-pill"
              style={{ marginTop: 8 }}
              onClick={() => {
                setSelectedJobPostId(null);
                setStatusFilter("ALL");
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="cand-table-wrapper">
          <table className="cand-table cand-table--compact">
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    style={{ cursor: "pointer" }}
                    checked={
                      filtered
                        .slice(
                          (tablePage - 1) * TABLE_PAGE_SIZE,
                          tablePage * TABLE_PAGE_SIZE,
                        )
                        .filter(isSchedulable)
                        .every((c) => selectedIds.has(c.appliedCandidateId)) &&
                      filtered
                        .slice(
                          (tablePage - 1) * TABLE_PAGE_SIZE,
                          tablePage * TABLE_PAGE_SIZE,
                        )
                        .filter(isSchedulable).length > 0
                    }
                    onChange={toggleSelectAll}
                    title="Select / deselect all on this page"
                  />
                </th>
                <th style={{ width: 48, textAlign: "center" }}>SL No</th>{" "}
                <th>Candidate</th>
                <th>Contact</th>
                <th>Qualification</th>
                <th>Skills</th>
                <th>Resume</th>
                <th>Expected Annual Salary</th>
                <th>Current Company</th>
                <th>Current Location</th>
                <th>Notice Period</th>
                <th>Applied On</th>
                <th>Applied Via</th>
                <th>Applied Jobs</th>
                <th>Slot Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered
                .slice(
                  (tablePage - 1) * TABLE_PAGE_SIZE,
                  tablePage * TABLE_PAGE_SIZE,
                )
                .map((c, idx) => (
                  <tr key={c.appliedCandidateId}>
                    <td style={{ textAlign: "center" }}>
                      {isSchedulable(c) ? (
                        <input
                          type="checkbox"
                          style={{ cursor: "pointer" }}
                          checked={selectedIds.has(c.appliedCandidateId)}
                          onChange={() => toggleSelectOne(c.appliedCandidateId)}
                        />
                      ) : (
                        <span
                          title={
                            c.scheduledInterview
                              ? "Already scheduled"
                              : normalizeInterviewStatus(
                                    c.applicationStatus ?? c.status,
                                  ) === "REJECTED"
                                ? "Candidate is rejected"
                                : TERMINAL_INTERVIEW_STATUSES.includes(
                                      normalizeInterviewStatus(
                                        c.interviewStatus,
                                      ),
                                    )
                                  ? `Candidate is ${normalizeInterviewStatus(c.interviewStatus).toLowerCase()}`
                                  : "No job post"
                          }
                          style={{ color: "#d1d5db", fontSize: "0.75rem" }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td className="cand-td-num">
                      {(tablePage - 1) * TABLE_PAGE_SIZE + idx + 1}
                    </td>
                    <td>
                      <div className="cand-identity">
                        <div
                          className="cand-avatar"
                          style={{ background: avatarColor(c.candidateName) }}
                        >
                          {getInitials(c.candidateName)}
                        </div>
                        <div>
                          <div className="cand-name">{c.candidateName}</div>
                          <div className="cand-email">{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="cand-contact">{c.contactNumber || "—"}</td>
                    <td>
                      <div>
                        <div>{c.qualification || "—"}</div>
                        {c.qualificationType && (
                          <div className="cand-email">
                            {c.qualificationType}
                          </div>
                        )}
                        {hasActiveFilters && c._score > 0 && (
                          <div className="cand-score-badge">
                            ★ {c._score} pts
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <SkillTags skills={c.skills} />
                    </td>
                    <td>
                      {c.resumeUrl ? (
                        <a
                          href={c.resumeUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="cand-detail-link"
                        >
                          View ↗
                        </a>
                      ) : (
                        <span className="cand-na">—</span>
                      )}
                    </td>
                    <td className="cand-salary">
                      {c.expectedSalary
                        ? `₹${Number(c.expectedSalary).toLocaleString()}`
                        : "—"}
                    </td>
                    <td>
                      <div>
                        <div>{c.currentCompany || "—"}</div>
                        {c.currentCompanyDesignation && (
                          <div className="cand-email">
                            {c.currentCompanyDesignation}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        <div>{c.currentLocation || "—"}</div>
                        {c.preferredWorkLocation && (
                          <div
                            className="cand-email"
                            title="Preferred work location"
                          >
                            Prefers: {c.preferredWorkLocation}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {c.noticePeriodDays != null
                        ? `${c.noticePeriodDays} day${c.noticePeriodDays !== 1 ? "s" : ""}`
                        : c.applicantJoiningStatus === "IMMEDIATE"
                          ? "Immediate"
                          : c.applicantJoiningStatus || "—"}
                    </td>
                    <td>{formatDate(c.appliedOn)}</td>
                    <td>
                      {c.sources?.length > 0 ? (
                        <div
                          style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                        >
                          {Array.from(
                            new Map(
                              (c.sources || []).map((s) => [s.id, s]),
                            ).values(),
                          ).map((s) => (
                            <span
                              key={s.id}
                              className="cand-source-tag"
                              title={
                                s.consultancyName
                                  ? `${s.consultancyName} (Connection #${s.connectionId ?? "—"})`
                                  : undefined
                              }
                            >
                              {s.sourceName}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <span>
                          {c.jobTitle || <span className="cand-na">—</span>}
                        </span>
                        {normalizeInterviewStatus(
                          c.applicationStatus ?? c.status,
                        ) === "REJECTED" ? (
                          // Application itself was rejected — always show
                          // "Rejected" rather than a stale/leftover
                          // interviewStatus like "NOT_SCHEDULED".
                          <InterviewStatusBadge status="REJECTED" />
                        ) : (
                          c.interviewStatus && (
                            <InterviewStatusBadge status={c.interviewStatus} />
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <RecentSlotLink url={c.recentSlotUrl} />
                    </td>
                    <td>
                      <div className="cand-row-actions">
                        <button
                          className="cand-row-btn cand-row-btn--view cand-row-btn--labeled"
                          onClick={() => setDetailTarget(c)}
                          title="View candidate details"
                        >
                          <EyeIcon />
                          <span>Details</span>
                        </button>
                        {c.sourceFrom !== "CONSULTANCY" && (
                          <button
                            className="cand-row-btn cand-row-btn--edit cand-row-btn--labeled"
                            onClick={() => {
                              setEditTarget(c);
                              setDrawerOpen(true);
                            }}
                            title="Edit"
                          >
                            <EditIcon />
                            <span>Edit</span>
                          </button>
                        )}
                        <button
                          className="cand-row-btn cand-row-btn--delete cand-row-btn--labeled"
                          onClick={() => handleDeleteClick(c)}
                          title="Delete"
                          disabled={deleteLoading}
                        >
                          <TrashIcon />
                          <span>Delete</span>
                        </button>
                        {c.jobPostId && (
                          <button
                            className="cand-row-btn cand-row-btn--timeline cand-row-btn--labeled"
                            onClick={() =>
                              setTimelineTarget(adaptForPipeline(c))
                            }
                            title="View Timeline"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <circle
                                cx="7"
                                cy="7"
                                r="5.5"
                                stroke="currentColor"
                                strokeWidth="1.3"
                              />
                              <path
                                d="M7 4v3l2 1.5"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                              />
                            </svg>
                            <span>Timeline</span>
                          </button>
                        )}
                        {isSchedulable(c) && (
                          <button
                            className="cand-row-btn cand-row-btn--schedule cand-row-btn--labeled"
                            onClick={() =>
                              setScheduleTarget(adaptForPipeline(c))
                            }
                            title="Schedule Interview"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <rect
                                x="1"
                                y="2"
                                width="12"
                                height="11"
                                rx="1.5"
                                stroke="currentColor"
                                strokeWidth="1.3"
                              />
                              <path
                                d="M4 1v2M10 1v2M1 5.5h12"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                              />
                              <path
                                d="M7 8v2M6 9h2"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                              />
                            </svg>
                            <span>Schedule</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <TablePagination
            page={tablePage}
            total={filtered.length}
            pageSize={TABLE_PAGE_SIZE}
            onChange={setTablePage}
          />
        </div>
      )}

      {drawerOpen && (
        <CompanyCandidateDrawer
          candidate={editTarget}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchCandidates();
          }}
        />
      )}
      {bulkUploadOpen && (
        <BulkUploadPopup
          apiBase={`${process.env.REACT_APP_API_HR_URL}/applyjob-bulk`}
          selectedJobPostId={selectedJobPostId}
          onClose={() => setBulkUploadOpen(false)}
          onUploaded={fetchCandidates}
        />
      )}
      {detailTarget && (
        <CompanyCandidateDetail
          candidate={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEdit={() => {
            setDetailTarget(null);
            setEditTarget(detailTarget);
            setDrawerOpen(true);
          }}
        />
      )}
      {timelineTarget && (
        <CandidateTimelinePanel
          candidate={timelineTarget}
          flows={flows}
          onClose={() => setTimelineTarget(null)}
        />
      )}
      {scheduleTarget && (
        <ScheduleModal
          candidate={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          onDone={() => {
            setScheduleTarget(null);
            fetchCandidates();
          }}
        />
      )}
      <WarningPopup
        isOpen={!!forceDeleteTarget}
        onClose={() => setForceDeleteTarget(null)}
        onDecline={() => setForceDeleteTarget(null)}
        onAccept={handleForceDeleteConfirm}
        title="⚠️ Force Delete Candidate"
        message={
          forceDeleteTarget?._conflictMessage
            ? `${forceDeleteTarget._conflictMessage} Force deleting will permanently remove all associated records. Proceed?`
            : `"${forceDeleteTarget?.candidateName}" is linked to application history. Force deleting will permanently remove all associated records. Proceed?`
        }
        acceptText={deleteLoading ? "Force Deleting…" : "Force Delete"}
        declineText="Cancel"
      />

      {bulkScheduleOpen && (
        <BulkScheduleModal
          candidates={selectedCandidates}
          onClose={() => setBulkScheduleOpen(false)}
          onDone={() => {
            setSelectedIds(new Set());
            fetchCandidates();
          }}
        />
      )}
    </>
  );
};

const BulkUploadPopup = ({
  apiBase,
  selectedJobPostId,
  onClose,
  onUploaded,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const handleDownloadTemplate = async () => {
    try {
      const templateUrl = selectedJobPostId
        ? `${apiBase}/template/simple`
        : `${apiBase}/template`;

      const res = await axios.get(templateUrl, {
        responseType: "blob",
      });

      const disposition = res.headers["content-disposition"];

      let filename = "candidate_template.xlsx";

      if (disposition) {
        const match =
          disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
          disposition.match(/filename="?([^"]+)"?/i);

        if (match?.[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download template.");
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadUrl = selectedJobPostId
        ? `${apiBase}/upload/${selectedJobPostId}`
        : `${apiBase}/upload`;
      const res = await axios.post(uploadUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res?.data?.message || "Candidates uploaded successfully.");
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  return (
    <div
      className="bulk-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bulk-modal">
        <div className="bulk-modal__header">
          <span className="bulk-modal__title">Bulk upload candidates</span>
          <button className="bulk-modal__close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="bulk-modal__body">
          <div className="bulk-step">
            <div className="bulk-step__num">1</div>
            <div className="bulk-step__content">
              <div className="bulk-step__label">Download the template</div>
              <div className="bulk-step__desc">
                Get the Excel template with required columns and fill in your
                candidate data.
              </div>
              <button
                className="bulk-download-btn"
                onClick={handleDownloadTemplate}
              >
                ↓ Download template
              </button>
            </div>
          </div>
          <div className="bulk-divider" />
          <div className="bulk-step">
            <div className="bulk-step__num">2</div>
            <div className="bulk-step__content">
              <div className="bulk-step__label">Upload the filled template</div>
              <div className="bulk-step__desc">
                Upload your completed file. Only .xlsx or .xls files accepted.
              </div>
              {!file ? (
                <div
                  className="bulk-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current.click()}
                >
                  <span className="bulk-dropzone__label">
                    Drag & drop or <u>browse</u>
                  </span>
                  <span className="bulk-dropzone__hint">
                    .xlsx or .xls only
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </div>
              ) : (
                <div className="bulk-file-chip">
                  <span className="bulk-file-chip__name">{file.name}</span>
                  <button onClick={() => setFile(null)}>✕</button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bulk-modal__footer">
          <button className="bulk-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`bulk-btn-upload ${file ? "bulk-btn-upload--ready" : ""}`}
            disabled={!file || uploading}
            onClick={handleUpload}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── AllCandidate ─────────────────────────────────────────────────────────────
const AllCandidate = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const location = useLocation();
  const [jobsTarget, setJobsTarget] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default"); // ← NEW

  const [tablePage, setTablePage] = useState(1);
  const TABLE_PAGE_SIZE = 50;

  const [filters, setFilters] = useState({
    skills: "",
    experienceMin: "",
    experienceMax: "",
    qualificationType: [],
    qualification: [],
  });
  const hasActiveFilters =
    !!filters.skills ||
    !!filters.experienceMin ||
    !!filters.experienceMax ||
    filters.qualificationType.length > 0 ||
    filters.qualification.length > 0;

  const navigate = useNavigate();

  useEffect(() => {
    setTablePage(1);
  }, [search, filters, sortBy]);

  const fetchCandidates = useCallback(() => {
    setLoading(true);
    axios
      .get(`${COMPANY_API}/all`)
      .then((res) => setCandidates(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (location.state?.jobPostId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleForceDeleteConfirm = async () => {
    if (!forceDeleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${COMPANY_API}/force/${forceDeleteTarget.recuirtmentCandidateId}`,
      );
      toast.success(res?.data?.message || "Candidate force deleted.");
      setForceDeleteTarget(null);
      fetchCandidates();
    } catch {
      toast.error("Force delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = async (target) => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${COMPANY_API}/${target.recuirtmentCandidateId}`);
      toast.success("Candidate deleted.");
      fetchCandidates();
    } catch (err) {
      if (err?.response?.status === 409) {
        setForceDeleteTarget({
          ...target,
          _conflictMessage: err?.response?.data?.message,
        });
      } else {
        toast.error(err?.response?.data?.message || "Failed to delete.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const result = candidates.filter((c) => {
      const matchSearch =
        !q ||
        c.candidateName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.skills?.toLowerCase().includes(q) ||
        c.qualification?.toLowerCase().includes(q);

      const matchSkills = (() => {
        if (!filters.skills) return true;
        const wanted = filters.skills
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        const has = (c.skills || "").toLowerCase();
        return wanted.some((s) => has.includes(s));
      })();

      const matchExpMin =
        !filters.experienceMin ||
        (c.experience != null &&
          Number(c.experience) >= Number(filters.experienceMin));

      const matchExpMax =
        !filters.experienceMax ||
        (c.experience != null &&
          Number(c.experience) <= Number(filters.experienceMax));

      const matchQualType =
        filters.qualificationType.length === 0 ||
        filters.qualificationType
          .map((q) => q.toUpperCase())
          .includes((c.qualificationType || "").toUpperCase());

      const matchQual =
        filters.qualification.length === 0 ||
        filters.qualification
          .map((q) => q.toLowerCase())
          .includes((c.qualification || "").toLowerCase());

      return (
        matchSearch &&
        matchSkills &&
        matchExpMin &&
        matchExpMax &&
        matchQualType &&
        matchQual
      );
    });

    return applySorting(result, sortBy); // ← NEW
  }, [candidates, search, filters, sortBy]);

  // ── Sidebar analytics ─────────────────────────────────────────────────────
  const qualDonutSegments = useMemo(() => {
    const QUAL_COLORS = [
      "#2563eb",
      "#0f6e56",
      "#d97706",
      "#6d28d9",
      "#dc2626",
      "#0ea5e9",
    ];
    const counts = {};
    candidates.forEach((c) => {
      const qt = c.qualificationType || "Unknown";
      counts[qt] = (counts[qt] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, value], i) => ({
        label,
        value,
        color: QUAL_COLORS[i % QUAL_COLORS.length],
      }));
  }, [candidates]);

  const topSkillsAll = useMemo(() => {
    const counts = {};
    candidates.forEach((c) => {
      (c.skills || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => {
          counts[s] = (counts[s] || 0) + 1;
        });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));
  }, [candidates]);

  const quickStats = useMemo(
    () => [
      { label: "Total candidates", value: candidates.length },
      {
        label: "With resume URL",
        value: candidates.filter((c) => c.resumeUrl).length,
      },
      {
        label: "With skills listed",
        value: candidates.filter((c) => c.skills).length,
      },
      {
        label: "From consultancy",
        value: candidates.filter((c) => c.sourceFrom === "CONSULTANCY").length,
      },
    ],
    [candidates],
  );

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Candidate Pool</h2>
            <span className="cand-page__count">
              {filtered.length} candidates
            </span>
          </div>
          <p className="cand-page__sub">
            {filtered.length} of {candidates.length} candidate
            {candidates.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="cand-top-action-btn"
            onClick={() => exportMyCandidatesCSV(filtered)}
            disabled={filtered.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>
          <button
            className="cand-top-action-btn"
            onClick={() => setBulkUploadOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1v9M5 4l3-3 3 3M1 11v1.5A1.5 1.5 0 0 0 2.5 14h11A1.5 1.5 0 0 0 15 12.5V11"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Bulk Upload
          </button>
          <button
            className="cand-create-btn"
            onClick={() => {
              setEditTarget(null);
              setDrawerOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Add Candidate
          </button>
        </div>
      </div>

      <div className="cand-toolbar">
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <FilterDropdown
            hasActiveFilters={hasActiveFilters}
            onClear={() =>
              setFilters({
                skills: "",
                experienceMin: "",
                experienceMax: "",
                qualificationType: [],
                qualification: [],
              })
            }
          >
            <div className="cand-filter-panel__grid">
              <F label="Skills (comma-separated)">
                <input
                  className="cand-input"
                  placeholder="e.g. React, Node, Excel"
                  value={filters.skills}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, skills: e.target.value }))
                  }
                />
              </F>
              <F label="Qualification Type">
                <MultiSelectDropdown
                  label="Qualification Type"
                  options={QUALIFICATION_TYPES}
                  selected={filters.qualificationType}
                  onChange={(val) =>
                    setFilters((p) => ({
                      ...p,
                      qualificationType: val,
                      qualification: [],
                    }))
                  }
                  placeholder="Any type"
                />
              </F>
              <F label="Qualification">
                <MultiSelectDropdown
                  label="Qualification"
                  options={
                    filters.qualificationType.length === 1
                      ? QUALIFICATION_OPTIONS[filters.qualificationType[0]] ||
                        []
                      : filters.qualificationType.length > 1
                        ? filters.qualificationType.flatMap(
                            (qt) => QUALIFICATION_OPTIONS[qt] || [],
                          )
                        : []
                  }
                  selected={filters.qualification}
                  onChange={(val) =>
                    setFilters((p) => ({ ...p, qualification: val }))
                  }
                  placeholder={
                    filters.qualificationType.length === 0
                      ? "Select type first"
                      : "Any qualification"
                  }
                />
              </F>
              <F label="Experience (years)">
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="cand-input"
                    type="number"
                    min="0"
                    placeholder="Min"
                    value={filters.experienceMin}
                    onChange={(e) =>
                      setFilters((p) => ({
                        ...p,
                        experienceMin: e.target.value,
                      }))
                    }
                  />
                  <input
                    className="cand-input"
                    type="number"
                    min="0"
                    placeholder="Max"
                    value={filters.experienceMax}
                    onChange={(e) =>
                      setFilters((p) => ({
                        ...p,
                        experienceMax: e.target.value,
                      }))
                    }
                  />
                </div>
              </F>
            </div>
          </FilterDropdown>
          <SortBar sortBy={sortBy} setSortBy={setSortBy} />
        </div>
        <SearchBar search={search} setSearch={setSearch} />
      </div>

      <div className="cand-content-grid">
        <div className="cand-main-content">
          {loading ? (
            <div className="cand-loader">Loading candidates...</div>
          ) : filtered.length === 0 ? (
            <div className="cand-empty">
              <span className="cand-empty__icon">👤</span>
              <p>
                {search
                  ? "No candidates match your search."
                  : "No candidates added yet."}
              </p>
            </div>
          ) : (
            <div className="cand-table-wrapper">
              <table className="cand-table">
                <thead>
                  <tr>
                    <th>SL No</th>
                    <th>Candidate</th>
                    <th>Contact</th>
                    <th>Qualification</th>
                    <th>Skills</th>
                    <th>Resume</th>
                    <th>Applied Via</th>
                    <th>Applied Jobs</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered
                    .slice(
                      (tablePage - 1) * TABLE_PAGE_SIZE,
                      tablePage * TABLE_PAGE_SIZE,
                    )
                    .map((c, idx) => (
                      <tr key={c.recuirtmentCandidateId}>
                        <td className="cand-td-num">
                          {(tablePage - 1) * TABLE_PAGE_SIZE + idx + 1}
                        </td>
                        <td>
                          <div className="cand-identity">
                            <div
                              className="cand-avatar"
                              style={{
                                background: avatarColor(c.candidateName),
                              }}
                            >
                              {getInitials(c.candidateName)}
                            </div>
                            <div>
                              <div className="cand-name">{c.candidateName}</div>
                              <div className="cand-email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="cand-contact">
                          {c.contactNumber || "—"}
                        </td>
                        <td>
                          <div>
                            <div>{c.qualification || "—"}</div>
                            {c.qualificationType && (
                              <div className="cand-email">
                                {c.qualificationType}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <SkillTags skills={c.skills} />
                        </td>
                        <td>
                          {c.resumeUrl ? (
                            <a
                              href={c.resumeUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="cand-detail-link"
                            >
                              View ↗
                            </a>
                          ) : (
                            <span className="cand-na">—</span>
                          )}
                        </td>
                        <td>
                          {c.sourceFrom ? (
                            <span className="cand-source-tag">
                              {c.sourceFrom}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <AppliedJobsHover
                            jobs={c.appliedJobs || []}
                            emptyLabel="No applications"
                          />
                        </td>
                        <td>
                          <div className="cand-row-actions">
                            <button
                              className="cand-row-btn cand-row-btn--jobs"
                              onClick={() => setJobsTarget(c)}
                              title="View Jobs"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <rect
                                  x="1"
                                  y="3"
                                  width="12"
                                  height="9"
                                  rx="1.5"
                                  stroke="currentColor"
                                  strokeWidth="1.4"
                                />
                                <path
                                  d="M5 3V2a2 2 0 0 1 4 0v1"
                                  stroke="currentColor"
                                  strokeWidth="1.4"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M1 7h12"
                                  stroke="currentColor"
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                  opacity=".5"
                                />
                              </svg>
                            </button>
                            <button
                              className="cand-row-btn cand-row-btn--view"
                              onClick={() => setDetailTarget(c)}
                              title="View"
                            >
                              <EyeIcon />
                            </button>
                            {c.sourceFrom !== "CONSULTANCY" && (
                              <button
                                className="cand-row-btn cand-row-btn--edit"
                                onClick={() => {
                                  setEditTarget(c);
                                  setDrawerOpen(true);
                                }}
                                title="Edit"
                              >
                                <EditIcon />
                              </button>
                            )}
                            <button
                              className="cand-row-btn cand-row-btn--delete"
                              onClick={() => handleDeleteClick(c)}
                              title="Delete"
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && candidates.length > 0 && (
          <aside className="hfd-sidebar">
            <CandSidebarWidget title="Qualification breakdown">
              <div className="hfd-donut-wrap">
                <CandDonutChart size={84} segments={qualDonutSegments} />
                <div className="hfd-donut-legend">
                  {qualDonutSegments.map(({ label, value, color }) => (
                    <div key={label} className="hfd-donut-legend__row">
                      <span
                        className="hfd-donut-legend__dot"
                        style={{ background: color }}
                      />
                      <span className="hfd-donut-legend__label">{label}</span>
                      <span className="hfd-donut-legend__val">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CandSidebarWidget>
            {topSkillsAll.length > 0 && (
              <CandSidebarWidget title="Top skills in pool">
                <CandSparkBars items={topSkillsAll} colorClass="amber" />
              </CandSidebarWidget>
            )}
            <CandSidebarWidget title="Pool overview">
              <CandSparkBars items={quickStats} />
            </CandSidebarWidget>
            <CandSidebarWidget title="Quick tips" tipsVariant>
              <ul className="hfd-tips">
                <li>
                  Use <strong>experience filters</strong> to narrow by years.
                </li>
                <li>
                  Click the <strong>briefcase icon</strong> to view all applied
                  jobs.
                </li>
                <li>Bulk upload candidates with the template to save time.</li>
              </ul>
            </CandSidebarWidget>
          </aside>
        )}
      </div>

      {drawerOpen && (
        <CompanyCandidateDrawer
          candidate={editTarget}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchCandidates();
          }}
        />
      )}
      {bulkUploadOpen && (
        <BulkUploadPopup
          apiBase={`${process.env.REACT_APP_API_HR_URL}/candidate-bulk`}
          onClose={() => setBulkUploadOpen(false)}
          onUploaded={fetchCandidates}
        />
      )}
      {detailTarget && (
        <CompanyCandidateDetail
          candidate={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEdit={() => {
            setDetailTarget(null);
            setEditTarget(detailTarget);
            setDrawerOpen(true);
          }}
        />
      )}
      {jobsTarget && (
        <CandidateJobsPopup
          candidate={jobsTarget}
          onClose={() => setJobsTarget(null)}
        />
      )}
      <WarningPopup
        isOpen={!!forceDeleteTarget}
        onClose={() => setForceDeleteTarget(null)}
        onDecline={() => setForceDeleteTarget(null)}
        onAccept={handleForceDeleteConfirm}
        title="⚠️ Force Delete Candidate"
        message={
          forceDeleteTarget?._conflictMessage
            ? `${forceDeleteTarget._conflictMessage} Force deleting will permanently remove all associated records. Proceed?`
            : `"${forceDeleteTarget?.candidateName}" is linked to application history. Force deleting will permanently remove all associated records. Proceed?`
        }
        acceptText={deleteLoading ? "Force Deleting…" : "Force Delete"}
        declineText="Cancel"
      />
    </>
  );
};

// ─── ConsultancyCandidates (company view) ─────────────────────────────────────
const ConsultancyCandidates = () => {
  const [connections, setConnections] = useState([]);
  const [connLoading, setConnLoading] = useState(true);
  const [selectedConn, setSelectedConn] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState("");
  const [search, setSearch] = useState("");
  const [expandedCand, setExpandedCand] = useState(null);
  const [sortBy, setSortBy] = useState("default"); // ← NEW

  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedConsultancy, setSelectedConsultancy] = useState("all");
  const [selectedJobPostId, setSelectedJobPostId] = useState(null);

  useEffect(() => {
    setConnLoading(true);
    axios
      .get(CONN_API)
      .then((r) => {
        const list = r.data.data || [];
        setConnections(list);
        if (list.length > 0)
          setSelectedConn({
            connectionId: list[0].connectionId,
            moduleId: list[0].moduleId,
          });
      })
      .catch(console.error)
      .finally(() => setConnLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedConn) return;
    setCandLoading(true);
    setCandError("");
    setCandidates([]);
    setSelectedJobPostId(null);
    axios
      .get(CONN_API)
      .then((r) => {
        const flat = r.data.data || [];
        // Filter to only the selected connection's candidates
        setCandidates(
          flat.filter((c) => c.connectionId === selectedConn.connectionId),
        );
      })
      .catch((err) =>
        setCandError(
          err?.response?.data?.message || "Failed to load candidates.",
        ),
      )
      .finally(() => setCandLoading(false));
  }, [selectedConn]);

  const allJobs = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const jobEntries = c.jobPosts || [];

      jobEntries.forEach((j) => {
        if (j.jobPostId && !map.has(j.jobPostId)) {
          map.set(j.jobPostId, {
            jobPostId: j.jobPostId,
            jobTitle: j.jobTitle,
            jobPostCode: j.jobPostCode,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [candidates]);

  const consultancyOptions = [
    ...new Set(candidates.map((c) => c.consultancyName).filter(Boolean)),
  ];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const result = candidates.filter((c) => {
      const matchSearch =
        !q ||
        c.candidateName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.skills?.toLowerCase().includes(q) ||
        (c.jobPosts || []).some((j) => j.jobTitle?.toLowerCase().includes(q));

      const matchConsultancyName =
        selectedConsultancy === "all" ||
        c.consultancyName === selectedConsultancy;

      const matchJob =
        !selectedJobPostId ||
        (c.jobPosts || []).some((j) => j.jobPostId === selectedJobPostId);

      const matchStatus =
        statusFilter === "all" ||
        (c.status || "PENDING").toUpperCase() === statusFilter.toUpperCase();

      const matchDate = (() => {
        if (dateFilter === "all") return true;
        const appliedDate = new Date(c.appliedOn);
        const today = new Date();
        const diff = (today - appliedDate) / (1000 * 60 * 60 * 24);
        if (dateFilter === "today") return diff < 1;
        if (dateFilter === "week") return diff <= 7;
        if (dateFilter === "month") return diff <= 30;
        return true;
      })();

      return (
        matchSearch &&
        matchConsultancyName &&
        matchJob &&
        matchStatus &&
        matchDate
      );
    });

    return applySorting(result, sortBy);
  }, [
    candidates,
    search,
    selectedConsultancy,
    selectedJobPostId,
    statusFilter,
    dateFilter,
    sortBy,
  ]);

  const consultancyStatusCounts = useMemo(() => {
    const counts = {};
    candidates.forEach((c) => {
      const s = (c.status || "PENDING").toUpperCase();
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [candidates]);

  const consultancyStats = useMemo(() => {
    const counts = {};
    candidates.forEach((c) => {
      const name = c.consultancyName || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));
  }, [candidates]);

  const topSkillsConsultancy = useMemo(() => {
    const counts = {};
    candidates.forEach((c) => {
      (c.skills || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((s) => {
          counts[s] = (counts[s] || 0) + 1;
        });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));
  }, [candidates]);

  const uniqueConnections = Object.values(
    connections.reduce((acc, curr) => {
      const key = curr.consultancyName || curr.companyName || curr.connectionId;
      if (!acc[key]) acc[key] = curr;
      return acc;
    }, {}),
  );

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Consultancy Candidates</h2>
            <span className="cand-page__count">
              {filtered.length} candidates
            </span>
          </div>
          <p className="cand-page__sub">
            Candidates shared by connected consultancy partners
          </p>
        </div>
      </div>

      {connLoading ? (
        <div className="cand-loader">Loading consultancy connections...</div>
      ) : connections.length === 0 ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">🤝</span>
          <p>No accepted consultancy connections found.</p>
        </div>
      ) : (
        <>
          <div className="cand-conn-bar">
            <span className="cand-conn-bar__label">Consultancy:</span>
            <div className="cand-conn-pills">
              {uniqueConnections.map((c) => (
                <button
                  key={c.connectionId}
                  className={`cand-conn-pill${selectedConn?.connectionId === c.connectionId ? " cand-conn-pill--active" : ""}`}
                  onClick={() => {
                    setSelectedConn({
                      connectionId: c.connectionId,
                      moduleId: c.moduleId,
                    });
                    setSearch("");
                  }}
                >
                  <span className="cand-conn-pill__dot" />
                  {c.consultancyName || `Connection #${c.connectionId}`}
                </button>
              ))}
            </div>
          </div>

          {!candLoading && allJobs.length > 0 && (
            <div className="cand-job-filter-bar">
              <button
                className={`cand-job-pill${!selectedJobPostId ? " cand-job-pill--active" : ""}`}
                onClick={() => setSelectedJobPostId(null)}
              >
                All Jobs
              </button>
              {allJobs.map((j) => (
                <button
                  key={j.jobPostId}
                  className={`cand-job-pill${selectedJobPostId === j.jobPostId ? " cand-job-pill--active" : ""}`}
                  onClick={() =>
                    setSelectedJobPostId(
                      selectedJobPostId === j.jobPostId ? null : j.jobPostId,
                    )
                  }
                >
                  {j.jobTitle}
                  {j.jobPostCode && (
                    <span
                      style={{
                        opacity: 0.6,
                        marginLeft: 4,
                        fontSize: "0.75em",
                      }}
                    >
                      #{j.jobPostCode}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <SearchBar
            search={search}
            setSearch={setSearch}
            style={{ marginBottom: "10px" }}
          />

          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 12,
              alignItems: "center",
            }}
          >
            {consultancyOptions.length > 0 && (
              <>
                {["all", ...consultancyOptions].map((name) => {
                  const isActive = selectedConsultancy === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      className={`cand-job-pill${isActive ? " cand-job-pill--active" : ""}`}
                      onClick={() => setSelectedConsultancy(name)}
                    >
                      {name === "all" ? "All Consultancies" : name}
                    </button>
                  );
                })}
                <span
                  style={{
                    width: 1,
                    height: 20,
                    background: "#e5e7eb",
                    flexShrink: 0,
                    margin: "0 2px",
                  }}
                />
              </>
            )}
            {[
              { value: "all", label: "All Statuses" },
              { value: "PENDING", label: "Pending" },
              { value: "SHORTLISTED", label: "Shortlisted" },
              { value: "REJECTED", label: "Rejected" },
              { value: "SELECTED", label: "Selected" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`cand-job-pill${statusFilter === opt.value ? " cand-job-pill--active" : ""}`}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
            <span
              style={{
                width: 1,
                height: 20,
                background: "#e5e7eb",
                flexShrink: 0,
                margin: "0 2px",
              }}
            />
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "week", label: "Last 7 Days" },
              { value: "month", label: "Last 30 Days" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`cand-job-pill${dateFilter === opt.value ? " cand-job-pill--active" : ""}`}
                onClick={() => setDateFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* ── Sort bar ─────────────────────────────────────────────────── */}
          <SortBar
            sortBy={sortBy}
            setSortBy={setSortBy}
            options={SORT_OPTIONS.filter(
              (o) =>
                !["exp_asc", "exp_desc", "salary_asc", "salary_desc"].includes(
                  o.key,
                ),
            )}
          />

          <div className="cand-content-grid">
            <div className="cand-main-content">
              {candLoading ? (
                <div className="cand-loader">Loading candidates...</div>
              ) : candError ? (
                <div className="cand-empty">
                  <span className="cand-empty__icon">⚠️</span>
                  <p>{candError}</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="cand-empty">
                  <span className="cand-empty__icon">📭</span>
                  <p>
                    {search || selectedJobPostId
                      ? "No candidates match."
                      : "No candidates shared yet."}
                  </p>
                  {selectedJobPostId && (
                    <button
                      className="cand-job-pill"
                      style={{ marginTop: 8 }}
                      onClick={() => setSelectedJobPostId(null)}
                    >
                      Clear job filter
                    </button>
                  )}
                </div>
              ) : (
                <div className="cand-table-wrapper">
                  <table className="cand-table">
                    <thead>
                      <tr>
                        <th>SL No</th>
                        <th>Candidate</th>
                        <th>Contact</th>
                        <th>Qualification</th>
                        <th>Skills</th>
                        <th>Resume</th>
                        <th>Applied Jobs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c, i) => {
                        const isExpanded = expandedCand === c.candidateId;
                        const jobEntries = c.jobPosts || [];
                        const visibleJobs = selectedJobPostId
                          ? jobEntries.filter(
                              (j) => j.jobPostId === selectedJobPostId,
                            )
                          : jobEntries;
                        const hasJobs = visibleJobs.length > 0;
                        return (
                          <React.Fragment key={c.candidateId}>
                            <tr
                              className={isExpanded ? "cand-tr--expanded" : ""}
                            >
                              <td className="cand-td-num">{i + 1}</td>
                              <td>
                                <div className="cand-identity">
                                  <div
                                    className="cand-avatar"
                                    style={{
                                      background: avatarColor(
                                        c.candidateName || "",
                                      ),
                                    }}
                                  >
                                    {getInitials(c.candidateName || "")}
                                  </div>
                                  <div>
                                    <div className="cand-name">
                                      {c.candidateName || "—"}
                                    </div>
                                    <div className="cand-email">
                                      {c.email || ""}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="cand-contact">
                                {c.contactNumber || "—"}
                              </td>
                              <td>{c.qualification || "—"}</td>
                              <td>
                                <SkillTags skills={c.skills} />
                              </td>
                              <td>
                                {c.resumeUrl ? (
                                  <a
                                    href={c.resumeUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="cand-detail-link"
                                  >
                                    View ↗
                                  </a>
                                ) : (
                                  <span className="cand-na">—</span>
                                )}
                              </td>
                              <td>
                                {hasJobs ? (
                                  <button
                                    className={`cand-jobs-toggle${isExpanded ? " cand-jobs-toggle--open" : ""}`}
                                    onClick={() =>
                                      setExpandedCand(
                                        isExpanded ? null : c.candidateId,
                                      )
                                    }
                                  >
                                    <svg
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                    >
                                      <path
                                        d="M2 4l4 4 4-4"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    {visibleJobs.length} job
                                    {visibleJobs.length !== 1 ? "s" : ""}
                                  </button>
                                ) : (
                                  <span className="cand-na">No jobs</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && hasJobs && (
                              <tr className="cand-jobs-row">
                                <td colSpan={6}>
                                  <div className="cand-jobs-panel">
                                    <div className="cand-jobs-panel__label">
                                      Applied Job Posts
                                    </div>
                                    <div className="cand-jobs-list">
                                      {visibleJobs.map((j) => (
                                        <div
                                          key={j.jobPostId}
                                          className="cand-job-card"
                                        >
                                          <div className="cand-job-card__left">
                                            <div className="cand-job-card__title">
                                              {j.jobTitle || "—"}
                                            </div>
                                            <div className="cand-job-card__code">
                                              #{j.jobPostCode}
                                            </div>
                                          </div>
                                          <div className="cand-job-card__right">
                                            <InterviewStatusBadge
                                              status={j.interviewStatus}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {!candLoading && candidates.length > 0 && (
              <aside className="hfd-sidebar">
                <CandSidebarWidget title="Status breakdown">
                  <div className="hfd-donut-wrap">
                    <CandDonutChart
                      size={84}
                      segments={[
                        {
                          value: consultancyStatusCounts["SHORTLISTED"] || 0,
                          color: "#2563eb",
                        },
                        {
                          value: consultancyStatusCounts["SELECTED"] || 0,
                          color: "#0f6e56",
                        },
                        {
                          value: consultancyStatusCounts["PENDING"] || 0,
                          color: "#d97706",
                        },
                        {
                          value: consultancyStatusCounts["REJECTED"] || 0,
                          color: "#dc2626",
                        },
                      ]}
                    />
                    <div className="hfd-donut-legend">
                      {[
                        {
                          label: "Shortlisted",
                          val: consultancyStatusCounts["SHORTLISTED"] || 0,
                          color: "#2563eb",
                        },
                        {
                          label: "Selected",
                          val: consultancyStatusCounts["SELECTED"] || 0,
                          color: "#0f6e56",
                        },
                        {
                          label: "Pending",
                          val: consultancyStatusCounts["PENDING"] || 0,
                          color: "#d97706",
                        },
                        {
                          label: "Rejected",
                          val: consultancyStatusCounts["REJECTED"] || 0,
                          color: "#dc2626",
                        },
                      ]
                        .filter((i) => i.val > 0)
                        .map(({ label, val, color }) => (
                          <div key={label} className="hfd-donut-legend__row">
                            <span
                              className="hfd-donut-legend__dot"
                              style={{ background: color }}
                            />
                            <span className="hfd-donut-legend__label">
                              {label}
                            </span>
                            <span className="hfd-donut-legend__val">{val}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </CandSidebarWidget>
                {consultancyStats.length > 0 && (
                  <CandSidebarWidget title="Candidates per consultancy">
                    <CandSparkBars
                      items={consultancyStats}
                      colorClass="amber"
                    />
                  </CandSidebarWidget>
                )}
                {topSkillsConsultancy.length > 0 && (
                  <CandSidebarWidget title="Top skills">
                    <CandSparkBars items={topSkillsConsultancy} />
                  </CandSidebarWidget>
                )}
                <CandSidebarWidget title="Quick tips" tipsVariant>
                  <ul className="hfd-tips">
                    <li>Switch between consultancies using the pills above.</li>
                    <li>
                      Filter by <strong>job post</strong> to see role-specific
                      candidates.
                    </li>
                    <li>
                      Date filter shows candidates added within a time range.
                    </li>
                    <li>
                      <strong>Consultancy-shared candidates</strong> cannot be
                      edited — contact the consultancy to update their details.
                    </li>
                  </ul>
                </CandSidebarWidget>
              </aside>
            )}
          </div>
        </>
      )}
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
//  CONSULTANT CANDIDATE PAGE
// ═════════════════════════════════════════════════════════════════════════════
const ConsultantCandidatePage = () => {
  const { hasRole } = useAuth();
  const isConsultant = hasRole("ROLE_CONSULTANT");
  const [tab, setTab] = useState("my");

  useEffect(() => {
    if (!isConsultant && tab === "recruiter") {
      setTab("my");
    }
  }, [isConsultant]); // eslint-disable-line

  return (
    <div className="cand-page">
      <div className="cand-tab-bar">
        {/* My Candidates — visible to ALL roles */}
        <button
          className={`cand-tab${tab === "my" ? " cand-tab--active" : ""}`}
          onClick={() => setTab("my")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle
              cx="7"
              cy="4.5"
              r="2.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M2 13c0-2.76 2.24-5 5-5s5 2.24 5 5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          My Candidates
        </button>

        {/* Applied Candidates — visible to ALL roles */}
        <button
          className={`cand-tab${tab === "shared" ? " cand-tab--active" : ""}`}
          onClick={() => setTab("shared")}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle
              cx="3"
              cy="7"
              r="2"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <circle
              cx="11"
              cy="3"
              r="2"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <circle
              cx="11"
              cy="11"
              r="2"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M5 6.2L9 4M5 7.8L9 10"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          Applied Candidates
        </button>

        {/* Recruiter Applied Candidates — ROLE_CONSULTANT only */}
        {isConsultant && (
          <button
            className={`cand-tab${tab === "recruiter" ? " cand-tab--active" : ""}`}
            onClick={() => setTab("recruiter")}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect
                x="1"
                y="3"
                width="12"
                height="9"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M5 3V2a2 2 0 014 0v1"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path d="M1 7h12" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            Recruiter Applied Candidates
          </button>
        )}
      </div>

      {tab === "my" && <ConsultantMyCandidates />}
      {tab === "shared" && <ConsultantSharedCandidates />}
      {tab === "recruiter" && isConsultant && (
        <ConsultantRecruiterAppliedCandidates />
      )}
    </div>
  );
};

// ─── ConsultantMyCandidates ────────────────────────────────────────────────────
const ConsultantMyCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default");
  const [shareTarget, setShareTarget] = useState(null);

  const fetchCandidates = useCallback(() => {
    setLoading(true);
    axios
      .get(CONSULTANT_API)
      .then((res) => setCandidates(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleDeleteClick = async (target) => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${CONSULTANT_API}/${target.id}`);
      toast.success("Candidate deleted.");
      fetchCandidates();
    } catch (err) {
      if (err?.response?.status === 409) {
        setDeleteTarget({
          ...target,
          _conflictMessage: err?.response?.data?.message,
        });
      } else {
        toast.error(err?.response?.data?.message || "Failed to delete.");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleForceDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${CONSULTANT_API}/${deleteTarget.id}/force`);
      toast.success("Candidate force deleted.");
      setDeleteTarget(null);
      fetchCandidates();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const result = candidates.filter(
      (c) =>
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.skills?.toLowerCase().includes(q) ||
        c.qualification?.toLowerCase().includes(q),
    );
    return applySorting(result, sortBy); // ← NEW
  }, [candidates, search, sortBy]);

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">My Candidates</h2>
            <span className="cand-page__count">
              {filtered.length} candidates
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            className="cand-top-action-btn"
            onClick={() => exportMyCandidatesCSV(filtered)}
            disabled={filtered.length === 0}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v8M5 7l3 3 3-3"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 13h12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            Export Excel
          </button>
          <button
            className="cand-create-btn cand-create-btn--secondary"
            onClick={() => setBulkUploadOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1v9M5 4l3-3 3 3M1 11v1.5A1.5 1.5 0 0 0 2.5 14h11A1.5 1.5 0 0 0 15 12.5V11"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Bulk Upload
          </button>
          <button
            className="cand-create-btn"
            onClick={() => {
              setEditTarget(null);
              setDrawerOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M2 8h12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Add Candidate
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <SortBar
          sortBy={sortBy}
          setSortBy={setSortBy} /* keep existing options prop if present */
        />
        <SearchBar search={search} setSearch={setSearch} />
      </div>

      {loading ? (
        <div className="cand-loader">Loading candidates...</div>
      ) : filtered.length === 0 ? (
        <div className="cand-empty">
          <span className="cand-empty__icon">👤</span>
          <p>
            {search ? "No candidates match your search." : "No candidates yet."}
          </p>
          {!search && (
            <button
              className="cand-create-btn"
              onClick={() => setDrawerOpen(true)}
            >
              Add your first candidate
            </button>
          )}
        </div>
      ) : (
        <div className="cand-table-wrapper">
          <table className="cand-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Contact</th>
                <th>Qualification</th>
                <th>Experience</th>
                <th>Skills</th>
                <th>Resume</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="cand-identity">
                      <div
                        className="cand-avatar"
                        style={{ background: avatarColor(c.name || "") }}
                      >
                        {getInitials(c.name || "")}
                      </div>
                      <div>
                        <div className="cand-name">{c.name || "—"}</div>
                        <div className="cand-email">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="cand-contact">{c.contactNumber || "—"}</td>
                  <td>
                    <div>
                      <div>{c.qualification || "—"}</div>
                      {c.qualificationType && (
                        <div className="cand-email">{c.qualificationType}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    {c.experience
                      ? `${c.experience} yr${c.experience !== 1 ? "s" : ""}`
                      : "—"}
                  </td>
                  <td>
                    <SkillTags skills={c.skills} />
                  </td>
                  <td>
                    {c.resumeUrl ? (
                      <a
                        href={c.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="cand-detail-link"
                      >
                        View ↗
                      </a>
                    ) : (
                      <span className="cand-na">—</span>
                    )}
                  </td>
                  <td>
                    <div className="cand-row-actions">
                      {/* existing */}
                      <button
                        className="cand-row-btn cand-row-btn--view"
                        onClick={() => setDetailTarget(c)}
                        title="View"
                      >
                        <EyeIcon />
                      </button>
                      <button
                        className="cand-row-btn cand-row-btn--edit"
                        onClick={() => {
                          setEditTarget(c);
                          setDrawerOpen(true);
                        }}
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="cand-row-btn cand-row-btn--delete"
                        onClick={() => handleDeleteClick(c)}
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>

                      {/* NEW: Share this candidate to a requirement */}
                      <button
                        className="cand-row-btn"
                        onClick={() => setShareTarget(c)}
                        title="Share Candidate"
                        style={{ color: "#0f6e56" }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <circle
                            cx="3"
                            cy="7"
                            r="1.5"
                            stroke="currentColor"
                            strokeWidth="1.3"
                          />
                          <circle
                            cx="11"
                            cy="2.5"
                            r="1.5"
                            stroke="currentColor"
                            strokeWidth="1.3"
                          />
                          <circle
                            cx="11"
                            cy="11.5"
                            r="1.5"
                            stroke="currentColor"
                            strokeWidth="1.3"
                          />
                          <path
                            d="M4.4 6.2l5.1-2.9M4.4 7.8l5.1 2.9"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <ConsultantCandidateDrawer
          candidate={editTarget}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchCandidates();
          }}
        />
      )}
      {detailTarget && (
        <ConsultantCandidateDetail
          candidate={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEdit={() => {
            setDetailTarget(null);
            setEditTarget(detailTarget);
            setDrawerOpen(true);
          }}
        />
      )}
      {bulkUploadOpen && (
        <BulkUploadPopup
          apiBase={`${process.env.REACT_APP_API_CONSULTANT_URL}/candidate-bulk`}
          onClose={() => setBulkUploadOpen(false)}
          onUploaded={fetchCandidates}
        />
      )}
      <WarningPopup
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDecline={() => setDeleteTarget(null)}
        onAccept={handleForceDelete}
        title="⚠️ Force Delete Candidate"
        message={
          deleteTarget?._conflictMessage
            ? `${deleteTarget._conflictMessage} Force deleting will set their reference to null in all records. Proceed?`
            : `"${deleteTarget?.name}" is linked to requirement history. Force deleting will set their reference to null in all records. Proceed?`
        }
        acceptText={deleteLoading ? "Force Deleting…" : "Force Delete"}
        declineText="Cancel"
      />

      {shareTarget && (
        <ShareModal
          preselectedCandidateId={shareTarget.id}
          onClose={() => setShareTarget(null)}
          onSuccess={() => {
            setShareTarget(null);
            toast.success("Candidate shared successfully.");
          }}
        />
      )}
    </>
  );
};

// ─── ConsultantSharedCandidates ───────────────────────────────────────────────
const ConsultantSharedCandidates = () => {
  const [allApplied, setAllApplied] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqError, setReqError] = useState("");
  const [selectedReq, setSelectedReq] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("default"); // ← NEW

  useEffect(() => {
    setReqLoading(true);
    setReqError("");
    Promise.all([axios.get(SHARED_API), axios.get(APPLIED_CANDIDATES_API)])
      .then(([sharedRes, appliedRes]) => {
        const sharedList = sharedRes.data.data || [];
        const appliedList = appliedRes.data.data || [];
        setAllApplied(appliedList);
        const jobMap = new Map();
        appliedList.forEach((item) => {
          if (!jobMap.has(item.jobPostId)) {
            const sharedMeta = sharedList.find((s) =>
              String(s.jobCode) === String(item.jobPostId) ||
              sharedList.some((s2) => s2.candidateId === item.candidateId)
                ? sharedList.find((s2) => s2.candidateId === item.candidateId)
                : null,
            );
            jobMap.set(item.jobPostId, {
              jobPostId: item.jobPostId,
              jobTitle: item.jobTitle,
              connectionString: sharedMeta?.connectionString || null,
              requirementId: sharedMeta?.requirementId || item.jobPostId,
              jobCode: sharedMeta?.jobCode || null,
            });
          }
        });
        const reqs = Array.from(jobMap.values());
        setRequirements(reqs);
        if (reqs.length > 0) setSelectedReq(reqs[0]);
      })
      .catch((err) =>
        setReqError(
          err?.response?.data?.message || "Failed to load requirements.",
        ),
      )
      .finally(() => setReqLoading(false));
  }, []);

  const applied = useMemo(() => {
    if (!selectedReq) return [];
    const q = search.toLowerCase();
    const result = allApplied.filter((c) => {
      const matchJob = c.jobPostId === selectedReq.jobPostId;
      const matchSearch =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q);
      return matchJob && matchSearch;
    });
    return applySorting(result, sortBy); // ← NEW
  }, [allApplied, selectedReq, search, sortBy]);

  if (reqLoading)
    return <div className="cand-loader">Loading shared requirements...</div>;
  if (reqError)
    return (
      <div className="cand-empty">
        <span className="cand-empty__icon">⚠️</span>
        <p>{reqError}</p>
      </div>
    );

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Consultancy Workflow</h2>
            <span className="cand-page__count">
              {requirements.length} candidates
            </span>
          </div>
          <p className="cand-page__sub">
            {selectedReq
              ? `${selectedReq.jobTitle || "Requirement"} · Job #${selectedReq.jobPostId}`
              : "Select a requirement to view applied candidates"}
          </p>
        </div>
      </div>

      <div className="cand-shared-layout">
        <div className="cand-shared-sidebar">
          <div className="cand-shared-sidebar__header">
            <span className="cand-shared-sidebar__title">Requirements</span>
            <span className="cand-shared-sidebar__count">
              {requirements.length}
            </span>
          </div>
          <div className="cand-shared-sidebar__list">
            {requirements.map((r) => {
              const isActive = selectedReq?.jobPostId === r.jobPostId;
              return (
                <button
                  key={r.jobPostId}
                  className={`cand-req-card${isActive ? " cand-req-card--active" : ""}`}
                  onClick={() => {
                    setSelectedReq(r);
                    setSearch("");
                  }}
                >
                  <div className="cand-req-card__top">
                    <span className="cand-req-card__role">
                      {r.jobTitle || "Requirement"}
                    </span>
                    <span className="cand-req-card__code">#{r.jobPostId}</span>
                  </div>
                  <div className="cand-req-card__bottom">
                    <span className="cand-req-card__company">
                      {r.connectionString || "—"}
                    </span>
                    {r.jobCode && (
                      <span className="cand-req-card__job">{r.jobCode}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="cand-shared-main">
          {selectedReq && (
            <div className="cand-shared-main__header">
              <div>
                <h3 className="cand-shared-main__title">
                  {selectedReq.jobTitle || "Requirement"}
                </h3>
                <p className="cand-shared-main__sub">
                  {selectedReq.connectionString || "—"} · Job #
                  {selectedReq.jobPostId}
                  {selectedReq.jobCode && ` · ${selectedReq.jobCode}`}
                </p>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <SortBar
              sortBy={sortBy}
              setSortBy={setSortBy} /* keep existing options prop if present */
            />
            <SearchBar search={search} setSearch={setSearch} />
          </div>

          {applied.length === 0 ? (
            <div className="cand-empty">
              <span className="cand-empty__icon">👥</span>
              <p>
                {search
                  ? "No candidates match your search."
                  : "No candidates have applied yet."}
              </p>
            </div>
          ) : (
            <div className="cand-table-wrapper">
              <table className="cand-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Candidate</th>
                    <th>Mobile</th>
                    <th>Skills</th>
                    <th>Resume</th>
                    <th>Interview Status</th>
                    <th>Scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {applied.map((c, i) => (
                    <tr key={`${c.candidateId}-${c.jobPostId}`}>
                      <td className="cand-td-num">{i + 1}</td>
                      <td>
                        <div className="cand-identity">
                          <div
                            className="cand-avatar"
                            style={{ background: avatarColor(c.name || "") }}
                          >
                            {getInitials(c.name || "")}
                          </div>
                          <div>
                            <div className="cand-name">{c.name || "—"}</div>
                            <div className="cand-email">{c.email || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="cand-contact">{c.contactNumber || "—"}</td>
                      <td>
                        <SkillTags skills={c.skills} />
                      </td>
                      <td>
                        {c.resumeUrl ? (
                          <a
                            href={c.resumeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="cand-detail-link"
                          >
                            📄 View
                          </a>
                        ) : (
                          <span className="cand-na">—</span>
                        )}
                      </td>
                      <td>
                        <InterviewStatusBadge status={c.status} />
                      </td>
                      <td>
                        {c.scheduledInterview ? (
                          <span className="cand-scheduled-badge">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <circle
                                cx="6"
                                cy="6"
                                r="5"
                                stroke="#16a34a"
                                strokeWidth="1.3"
                              />
                              <path
                                d="M3.5 6l1.8 1.8L8.5 4"
                                stroke="#16a34a"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Scheduled
                          </span>
                        ) : (
                          <span className="cand-na">Not yet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const ConsultantRecruiterAppliedCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedJobPostId, setSelectedJobPostId] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default"); // ← NEW

  const jobGroups = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const key = c.jobPostId;
      if (!map.has(key)) {
        map.set(key, {
          jobPostId: key,
          jobTitle: c.jobTitle,
          candidateRequirementId: c.candidateRequirementId,
          candidates: [],
        });
      }
      map.get(key).candidates.push(c);
    });
    return Array.from(map.values());
  }, [candidates]);

  const fetchCandidates = useCallback(() => {
    setLoading(true);
    setError("");
    axios
      .get(RECRUITER_APPLIED_API)
      .then((r) => {
        const data = r.data.data || [];
        setCandidates(data);
        if (data.length > 0) setSelectedJobPostId(data[0].jobPostId);
      })
      .catch((err) =>
        setError(err?.response?.data?.message || "Failed to load candidates."),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const result = candidates.filter((c) => {
      const matchSearch =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.skills?.toLowerCase().includes(q);
      const matchJob = !selectedJobPostId || c.jobPostId === selectedJobPostId;
      return matchSearch && matchJob;
    });
    return applySorting(result, sortBy); // ← NEW
  }, [candidates, search, selectedJobPostId, sortBy]);

  const selectedGroup = jobGroups.find(
    (g) => g.jobPostId === selectedJobPostId,
  );

  if (loading) return <div className="cand-loader">Loading candidates...</div>;
  if (error)
    return (
      <div className="cand-empty">
        <span className="cand-empty__icon">⚠️</span>
        <p>{error}</p>
      </div>
    );

  return (
    <>
      <div className="cand-page__header">
        <div className="cand-page__header-main">
          <div className="cand-page__title-wrap">
            <h2 className="cand-page__title">Recruiter Applied Candidates</h2>
            <span className="cand-page__count">
              {filtered.length} candidates
            </span>
          </div>
          <p className="cand-page__sub">
            Candidates submitted by recruiters for shared requirements
          </p>
        </div>
        {selectedGroup && (
          <button
            className="cand-create-btn"
            onClick={() => setShareModalOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle
                cx="4"
                cy="8"
                r="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle
                cx="12"
                cy="3"
                r="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle
                cx="12"
                cy="13"
                r="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M6 7.2L10 4.5M6 8.8L10 11.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Share Candidates
          </button>
        )}
      </div>

      <div className="cand-shared-layout">
        <div className="cand-shared-sidebar">
          <div className="cand-shared-sidebar__header">
            <span className="cand-shared-sidebar__title">Job Posts</span>
            <span className="cand-shared-sidebar__count">
              {jobGroups.length}
            </span>
          </div>
          <div className="cand-shared-sidebar__list">
            {jobGroups.map((g) => {
              const isActive = selectedJobPostId === g.jobPostId;
              return (
                <button
                  key={g.jobPostId}
                  className={`cand-req-card${isActive ? " cand-req-card--active" : ""}`}
                  onClick={() => {
                    setSelectedJobPostId(g.jobPostId);
                    setSearch("");
                  }}
                >
                  <div className="cand-req-card__top">
                    <span className="cand-req-card__role">
                      {g.jobTitle || `Job #${g.jobPostId}`}
                    </span>
                    <span className="cand-req-card__code">#{g.jobPostId}</span>
                  </div>
                  <div className="cand-req-card__bottom">
                    <span className="cand-req-card__company">
                      {g.candidates.length} candidate
                      {g.candidates.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="cand-shared-main">
          {selectedGroup && (
            <div className="cand-shared-main__header">
              <div>
                <h3 className="cand-shared-main__title">
                  {selectedGroup.jobTitle || `Job #${selectedGroup.jobPostId}`}
                </h3>
                <p className="cand-shared-main__sub">
                  {filtered.length} candidate{filtered.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <SortBar
              sortBy={sortBy}
              setSortBy={setSortBy} /* keep existing options prop if present */
            />
            <SearchBar search={search} setSearch={setSearch} />
          </div>

          {filtered.length === 0 ? (
            <div className="cand-empty">
              <span className="cand-empty__icon">👥</span>
              <p>
                {search
                  ? "No candidates match your search."
                  : "No candidates for this job post."}
              </p>
              {search && (
                <button
                  className="cand-job-pill"
                  style={{ marginTop: 8 }}
                  onClick={() => setSearch("")}
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="cand-table-wrapper">
              <table className="cand-table">
                <thead>
                  <tr>
                    <th>SL No</th>
                    <th>Candidate</th>
                    <th>Mobile</th>
                    <th>Skills</th>
                    <th>Resume</th>
                    <th>Experience</th>
                    <th>Interview Status</th>
                    <th>Scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => (
                    <tr key={`${c.candidateId}-${c.jobPostId}`}>
                      <td className="cand-td-num">{i + 1}</td>
                      <td>
                        <div className="cand-identity">
                          <div
                            className="cand-avatar"
                            style={{ background: avatarColor(c.name || "") }}
                          >
                            {getInitials(c.name || "")}
                          </div>
                          <div>
                            <div className="cand-name">{c.name || "—"}</div>
                            <div className="cand-email">{c.email || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="cand-contact">{c.contactNumber || "—"}</td>
                      <td>
                        <SkillTags skills={c.skills} />
                      </td>
                      <td>
                        {c.resumeUrl ? (
                          <a
                            href={c.resumeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="cand-detail-link"
                          >
                            📄 View
                          </a>
                        ) : (
                          <span className="cand-na">—</span>
                        )}
                      </td>
                      <td>
                        {c.experience
                          ? `${c.experience} yr${c.experience !== 1 ? "s" : ""}`
                          : "—"}
                      </td>
                      <td>
                        <InterviewStatusBadge status={c.status} />
                      </td>
                      <td>
                        {c.scheduledInterview ? (
                          <span className="cand-scheduled-badge">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <circle
                                cx="6"
                                cy="6"
                                r="5"
                                stroke="#16a34a"
                                strokeWidth="1.3"
                              />
                              <path
                                d="M3.5 6l1.8 1.8L8.5 4"
                                stroke="#16a34a"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Scheduled
                          </span>
                        ) : (
                          <span className="cand-na">Not yet</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {shareModalOpen && (
        <ShareModal
          preselectedRequirementId={selectedGroup?.candidateRequirementId}
          onClose={() => setShareModalOpen(false)}
          onSuccess={() => {
            setShareModalOpen(false);
            fetchCandidates();
          }}
        />
      )}
    </>
  );
};

// ─── ConsultantCandidateDrawer ────────────────────────────────────────────────
const emptyConsultantForm = {
  name: "",
  email: "",
  contactNumber: "",
  resumeUrl: "",
  portfolioLink: "",
  skills: "",
  qualificationType: "",
  qualification: "",
  experience: "",
  idProof: "",
};

const normalizeQualification = (q = "") => {
  const map = {
    "BCA (Computer Applications)": "BCA",
    "BBA (Business Administration)": "BBA",
    "B.Tech (Engineering)": "B.E / B.Tech",
    "MCA (Computer Applications)": "MCA",
    "MBA (Business Administration)": "MBA",
  };
  return map[q] || q;
};

const ConsultantCandidateDrawer = ({ candidate, onClose, onSaved }) => {
  const isEdit = !!candidate;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: candidate.name || "",
          email: candidate.email || "",
          contactNumber: candidate.contactNumber || "",
          resumeUrl: candidate.resumeUrl || "",
          portfolioLink: candidate.portfolioLink || "",
          skills: candidate.skills || "",
          expectedSalary: candidate.expectedSalary || "",
          qualificationType: candidate.qualificationType
            ? candidate.qualificationType.toUpperCase().replace(/[\s'-]/g, "_")
            : "",
          qualification: candidate.qualification
            ? normalizeQualification(candidate.qualification)
            : "",
          experience: candidate.experience || "",
          idProof: candidate.idProof || "",
        }
      : { ...emptyConsultantForm },
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({
      ...p,
      [name]: value,
      ...(name === "qualificationType" ? { qualification: "" } : {}),
    }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.contactNumber.trim()) e.contactNumber = "Required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    const payload = {
      name: form.name,
      email: form.email,
      contactNumber: form.contactNumber,
      resumeUrl: form.resumeUrl || undefined,
      portfolioLink: form.portfolioLink || undefined,
      skills: form.skills || undefined,
      qualificationType: form.qualificationType || undefined,
      qualification: form.qualification || undefined,
      experience: form.experience ? Number(form.experience) : undefined,
      idProof: form.idProof || undefined,
    };
    try {
      if (isEdit) {
        await axios.put(`${CONSULTANT_API}/${candidate.id}`, payload);
        toast.success("Candidate updated!");
      } else {
        await axios.post(CONSULTANT_API, payload);
        toast.success("Candidate added!");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save candidate.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">
              {isEdit ? "Edit Candidate" : "Add Candidate"}
            </h2>
            <p className="drawer__sub">
              {isEdit
                ? "Update candidate information"
                : "Fill in candidate details"}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          <div className="cand-section-label">Personal Information</div>
          <div className="cand-grid-2">
            <F label="Full Name" required error={errors.name}>
              <input
                className={`cand-input${errors.name ? " cand-input--error" : ""}`}
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Ravi Kumar"
              />
            </F>
            <F label="Email Address" required error={errors.email}>
              <input
                className={`cand-input${errors.email ? " cand-input--error" : ""}`}
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="ravi@example.com"
              />
            </F>
            <F label="Contact Number" required error={errors.contactNumber}>
              <input
                className={`cand-input${errors.contactNumber ? " cand-input--error" : ""}`}
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleChange}
                placeholder="+91 98765 43210"
              />
            </F>
            <F label="ID Proof">
              <input
                className="cand-input"
                name="idProof"
                value={form.idProof}
                onChange={handleChange}
                placeholder="Aadhaar / PAN / Passport"
              />
            </F>
          </div>
          <div className="cand-section-label" style={{ marginTop: 20 }}>
            Professional Details
          </div>
          <div className="cand-grid-2">
            <F label="Qualification Type">
              <select
                className="cand-input"
                name="qualificationType"
                value={form.qualificationType}
                onChange={handleChange}
              >
                <option value="">Select type</option>
                {QUALIFICATION_TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </F>
            <F label="Qualification">
              <select
                className="cand-input"
                name="qualification"
                value={form.qualification}
                onChange={handleChange}
                disabled={!form.qualificationType}
              >
                <option value="">
                  {form.qualificationType
                    ? "Select qualification"
                    : "Select a type first"}
                </option>
                {(QUALIFICATION_OPTIONS[form.qualificationType] || []).map(
                  (q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ),
                )}
              </select>
            </F>
            <F label="Experience (years)">
              <input
                className="cand-input"
                type="number"
                min="0"
                name="experience"
                value={form.experience}
                onChange={handleChange}
                placeholder="e.g. 3"
              />
            </F>
            <F label="Resume URL">
              <input
                className="cand-input"
                name="resumeUrl"
                value={form.resumeUrl}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
              />
            </F>
            <F label="Portfolio Link">
              <input
                className="cand-input"
                name="portfolioLink"
                value={form.portfolioLink}
                onChange={handleChange}
                placeholder="https://portfolio.example.com"
              />
            </F>
          </div>
          <div style={{ marginTop: 4 }}>
            <F label="Skills">
              <input
                className="cand-input"
                name="skills"
                value={form.skills}
                onChange={handleChange}
                placeholder="e.g. Communication, Sales, Excel"
              />
            </F>
          </div>
        </div>
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <span className="cv-btn__spinner" />
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Add Candidate"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

const ConsultantCandidateDetail = ({ candidate: c, onClose, onEdit }) => (
  <>
    <div className="drawer-backdrop" onClick={onClose} />
    <div className="drawer">
      <div className="drawer__header">
        <div className="cand-detail-hero">
          <div
            className="cand-avatar cand-avatar--lg"
            style={{ background: avatarColor(c.name || "") }}
          >
            {getInitials(c.name || "")}
          </div>
          <div>
            <h2 className="drawer__title">{c.name}</h2>
            <p className="drawer__sub">{c.email}</p>
          </div>
        </div>
        <button className="drawer__close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="drawer__body">
        <div className="cand-detail-grid">
          <DetailRow icon="📞" label="Contact" value={c.contactNumber || "-"} />
          <DetailRow
            icon="🎓"
            label="Qualification"
            value={c.qualification || "-"}
          />
          <DetailRow
            icon="📚"
            label="Qualification Type"
            value={c.qualificationType || "-"}
          />
          <DetailRow
            icon="🕐"
            label="Experience"
            value={
              c.experience
                ? `${c.experience} year${c.experience !== 1 ? "s" : ""}`
                : "-"
            }
          />
          <DetailRow
            icon="💰"
            label="Expected Annual Salary"
            value={
              c.expectedSalary
                ? `₹${Number(c.expectedSalary).toLocaleString()}`
                : "-"
            }
          />
          <DetailRow icon="🪪" label="ID Proof" value={c.idProof || "-"} />
          <DetailRow icon="📡" label="Source" value={c.sourceFrom || "-"} />
          <div className="cand-detail-row cand-detail-row--full">
            <span className="cand-detail-row__icon">📄</span>
            <div>
              <div className="cand-detail-row__label">Resume</div>
              {c.resumeUrl ? (
                <a
                  href={c.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="cand-detail-link"
                >
                  View Resume ↗
                </a>
              ) : (
                <div className="cand-na">—</div>
              )}
            </div>
          </div>
          <div className="cand-detail-row cand-detail-row--full">
            <span className="cand-detail-row__icon">🔗</span>
            <div>
              <div className="cand-detail-row__label">Portfolio</div>
              {c.portfolioLink ? (
                <a
                  href={c.portfolioLink}
                  target="_blank"
                  rel="noreferrer"
                  className="cand-detail-link"
                >
                  View Portfolio ↗
                </a>
              ) : (
                <div className="cand-na">—</div>
              )}
            </div>
          </div>
          <div className="cand-detail-row cand-detail-row--full">
            <span className="cand-detail-row__icon">🛠️</span>
            <div>
              <div className="cand-detail-row__label">Skills</div>
              {c.skills ? (
                <div className="cand-skills cand-skills--detail">
                  {c.skills
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((s) => (
                      <span key={s} className="cand-skill-tag">
                        {s}
                      </span>
                    ))}
                </div>
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="drawer__footer">
        <button className="cv-btn cv-btn--ghost" onClick={onClose}>
          Close
        </button>
        <div style={{ flex: 1 }} />
        <button className="cv-btn cv-btn--primary" onClick={onEdit}>
          Edit Candidate
        </button>
      </div>
    </div>
  </>
);

// ─── Company candidate drawer + detail ───────────────────────────────────────
const emptyCompanyForm = {
  candidateName: "",
  email: "",
  contactNumber: "",
  qualificationType: "",
  qualification: "",
  resumeUrl: "",
  portfolioLink: "",
  idProof: "",
  skills: "",
  sourceFrom: "SELF",
  experience: "",
};

const CompanyCandidateDrawer = ({ candidate, onClose, onSaved }) => {
  const isEdit = !!candidate;
  const [form, setForm] = useState(
    isEdit
      ? {
          candidateName: candidate.candidateName || "",
          email: candidate.email || "",
          contactNumber: candidate.contactNumber || "",
          qualificationType: candidate.qualificationType
            ? candidate.qualificationType.toUpperCase().replace(/[\s'-]/g, "_")
            : "",
          qualification: candidate.qualification
            ? normalizeQualification(candidate.qualification)
            : "",
          resumeUrl: candidate.resumeUrl || "",
          portfolioLink: candidate.portfolioLink || "",
          idProof: candidate.idProof || "",
          skills: candidate.skills || "",
          expectedSalary: candidate.expectedSalary || "",
          sourceFrom: candidate.sourceFrom || "",
          experience: candidate.experience || "",
        }
      : { ...emptyCompanyForm },
  );
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({
      ...p,
      [name]: value,
      ...(name === "qualificationType" ? { qualification: "" } : {}),
    }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.candidateName.trim()) e.candidateName = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.contactNumber.trim()) e.contactNumber = "Required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    const payload = {
      candidateName: form.candidateName,
      email: form.email,
      contactNumber: form.contactNumber,
      qualificationType: form.qualificationType || null,
      qualification: form.qualification || null,
      resumeUrl: form.resumeUrl || null,
      portfolioLink: form.portfolioLink || null,
      idProof: form.idProof || null,
      skills: form.skills || null,
      sourceFrom: form.sourceFrom || null,
      experience: form.experience || null,
    };
    try {
      if (isEdit) {
        await axios.put(
          `${COMPANY_API}/${candidate.recuirtmentCandidateId}`,
          payload,
        );
        toast.success("Candidate updated!");
      } else {
        await axios.post(COMPANY_API, payload);
        toast.success("Candidate added!");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer">
        <div className="drawer__header">
          <div>
            <h2 className="drawer__title">
              {isEdit ? "Edit Candidate" : "Add Candidate"}
            </h2>
            <p className="drawer__sub">
              {isEdit
                ? "Update candidate information"
                : "Fill in the candidate's details"}
            </p>
          </div>
          <button className="drawer__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="drawer__body">
          <div className="cand-section-label">Personal Information</div>
          <div className="cand-grid-2">
            <F label="Full Name" required error={errors.candidateName}>
              <input
                className={`cand-input${errors.candidateName ? " cand-input--error" : ""}`}
                name="candidateName"
                value={form.candidateName}
                onChange={handleChange}
                placeholder="e.g. Ravi Kumar"
              />
            </F>
            <F label="Email Address" required error={errors.email}>
              <input
                className={`cand-input${errors.email ? " cand-input--error" : ""}`}
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="ravi@example.com"
              />
            </F>
            <F label="Contact Number" required error={errors.contactNumber}>
              <input
                className={`cand-input${errors.contactNumber ? " cand-input--error" : ""}`}
                name="contactNumber"
                value={form.contactNumber}
                onChange={handleChange}
                placeholder="+91 98765 43210"
              />
            </F>
            <F label="ID Proof">
              <input
                className="cand-input"
                name="idProof"
                value={form.idProof}
                onChange={handleChange}
                placeholder="Aadhaar / PAN / Passport number"
              />
            </F>
          </div>
          <div className="cand-section-label" style={{ marginTop: 20 }}>
            Professional Details
          </div>
          <div className="cand-grid-2">
            <F label="Qualification Type">
              <select
                className="cand-input"
                name="qualificationType"
                value={form.qualificationType}
                onChange={handleChange}
              >
                <option value="">Select type</option>
                {QUALIFICATION_TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </F>
            <F label="Qualification">
              <select
                className="cand-input"
                name="qualification"
                value={form.qualification}
                onChange={handleChange}
                disabled={!form.qualificationType}
              >
                <option value="">
                  {form.qualificationType
                    ? "Select qualification"
                    : "Select a type first"}
                </option>
                {(QUALIFICATION_OPTIONS[form.qualificationType] || []).map(
                  (q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ),
                )}
              </select>
            </F>
            <F label="Experience (years)">
              <input
                className="cand-input"
                type="number"
                min="0"
                name="experience"
                value={form.experience}
                onChange={handleChange}
                placeholder="e.g. 3"
              />
            </F>
            <F label="Resume URL">
              <input
                className="cand-input"
                name="resumeUrl"
                value={form.resumeUrl}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
              />
            </F>
            <F label="Portfolio Link">
              <input
                className="cand-input"
                name="portfolioLink"
                value={form.portfolioLink}
                onChange={handleChange}
                placeholder="https://portfolio.example.com"
              />
            </F>
          </div>
          <div style={{ marginTop: 4 }}>
            <F label="Skills">
              <input
                className="cand-input"
                name="skills"
                value={form.skills}
                onChange={handleChange}
                placeholder="e.g. Communication, Sales, Excel"
              />
            </F>
          </div>
        </div>
        <div className="drawer__footer">
          <button className="cv-btn cv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="cv-btn cv-btn--submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <span className="cv-btn__spinner" />
            ) : isEdit ? (
              "Save Changes"
            ) : (
              "Add Candidate"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

const CompanyCandidateDetail = ({ candidate: c, onClose, onEdit }) => (
  <>
    <div className="drawer-backdrop" onClick={onClose} />
    <div className="drawer">
      <div className="drawer__header">
        <div className="cand-detail-hero">
          <div
            className="cand-avatar cand-avatar--lg"
            style={{ background: avatarColor(c.candidateName) }}
          >
            {getInitials(c.candidateName)}
          </div>
          <div>
            <h2 className="drawer__title">{c.candidateName}</h2>
            <p className="drawer__sub">{c.email}</p>
          </div>
        </div>
        <button className="drawer__close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="drawer__body">
        <div className="cand-detail-grid">
          <DetailRow icon="📞" label="Contact" value={c.contactNumber} />
          <DetailRow icon="🎓" label="Qualification" value={c.qualification} />
          <DetailRow
            icon="📚"
            label="Qualification Type"
            value={c.qualificationType}
          />
          <DetailRow
            icon="💰"
            label="Expected Annual Salary"
            value={
              c.expectedSalary
                ? `₹${Number(c.expectedSalary).toLocaleString()}`
                : null
            }
          />
          <DetailRow icon="🪪" label="ID Proof" value={c.idProof} />
          <DetailRow icon="📡" label="Source" value={c.sourceFrom} />
          <DetailRow
            icon="🏢"
            label="Current Company"
            value={c.currentCompany}
          />
          <DetailRow
            icon="💼"
            label="Current Designation"
            value={c.currentCompanyDesignation}
          />
          <DetailRow
            icon="📍"
            label="Current Location"
            value={c.currentLocation}
          />
          <DetailRow
            icon="🎯"
            label="Preferred Work Location"
            value={c.preferredWorkLocation}
          />
          <DetailRow
            icon="⏳"
            label="Notice Period"
            value={
              c.noticePeriodDays != null
                ? `${c.noticePeriodDays} day${c.noticePeriodDays !== 1 ? "s" : ""}`
                : null
            }
          />
          <DetailRow
            icon="🚦"
            label="Joining Status"
            value={
              c.applicantJoiningStatus
                ? c.applicantJoiningStatus
                    .toLowerCase()
                    .replace(/_/g, " ")
                    .replace(/^\w/, (ch) => ch.toUpperCase())
                : null
            }
          />
          <DetailRow
            icon="📅"
            label="Expected Joining Date"
            value={
              c.expectedJoiningDate ? formatDate(c.expectedJoiningDate) : null
            }
          />
          <DetailRow
            icon="🗓️"
            label="Expected Closing Date"
            value={
              c.expectedClosingDate ? formatDate(c.expectedClosingDate) : null
            }
          />
          <DetailRow
            icon="📥"
            label="Applied On"
            value={c.appliedOn ? formatDate(c.appliedOn) : null}
          />
          <DetailRow
            icon="🏷️"
            label="Application Status"
            value={c.applicationStatus}
          />
          <DetailRow
            icon="🧾"
            label="Candidate Status"
            value={c.candidateStatus}
          />
          <DetailRow
            icon="💬"
            label="Candidate Message"
            value={c.candidateMessage}
          />
          {c.resumeUrl && (
            <div className="cand-detail-row cand-detail-row--full">
              <span className="cand-detail-row__icon">📄</span>
              <div>
                <div className="cand-detail-row__label">Resume</div>
                <a
                  href={c.resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="cand-detail-link"
                >
                  View Resume ↗
                </a>
              </div>
            </div>
          )}
          {c.portfolioLink && (
            <div className="cand-detail-row cand-detail-row--full">
              <span className="cand-detail-row__icon">🔗</span>
              <div>
                <div className="cand-detail-row__label">Portfolio</div>
                <a
                  href={c.portfolioLink}
                  target="_blank"
                  rel="noreferrer"
                  className="cand-detail-link"
                >
                  View Portfolio ↗
                </a>
              </div>
            </div>
          )}
        </div>
        {c.skills && (
          <div style={{ marginTop: 20 }}>
            <div className="cand-section-label">Skills</div>
            <div className="cand-skills cand-skills--detail">
              {c.skills
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => (
                  <span key={s} className="cand-skill-tag">
                    {s}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
      <div className="drawer__footer">
        <button className="cv-btn cv-btn--ghost" onClick={onClose}>
          Close
        </button>
        <div style={{ flex: 1 }} />
        <button className="cv-btn cv-btn--primary" onClick={onEdit}>
          Edit Candidate
        </button>
      </div>
    </div>
  </>
);

const AppliedJobsHover = ({ jobs = [], emptyLabel = "—" }) => {
  if (!jobs.length) return <span className="cand-na">{emptyLabel}</span>;
  return (
    <div
      className="cand-jobs-compact"
      title={jobs.map((j) => j.jobTitle || "—").join("\n")}
    >
      <span className="cand-jobs-compact__count">{jobs.length}</span>
      <span className="cand-jobs-compact__label">
        {jobs.length === 1 ? "job" : "jobs"}
      </span>
      <div className="cand-jobs-compact__tooltip">
        {jobs.map((j, index) => (
          <div
            key={
              j.appliedCandidateId || j.jobPostId || `${j.jobTitle}-${index}`
            }
            className="cand-jobs-compact__row"
          >
            <span className="cand-jobs-compact__title">
              {j.jobTitle || "—"}
            </span>
            {j.interviewStatus && (
              <InterviewStatusBadge status={j.interviewStatus} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const CandDonutChart = ({ segments, size = 72 }) => {
  const r = (size - 12) / 2;
  const cx = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0)
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="#f3f4f6" />
        <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
        <text
          x={cx}
          y={cx + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          fill="#1e293b"
        >
          0
        </text>
      </svg>
    );
  let cumulative = 0;
  const slices = segments
    .filter((s) => s.value > 0)
    .map((seg) => {
      const pct = seg.value / total;
      const start = cumulative;
      cumulative += pct;
      const startAngle = start * 2 * Math.PI - Math.PI / 2;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cx + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cx + r * Math.sin(endAngle);
      const large = pct > 0.5 ? 1 : 0;
      return {
        ...seg,
        d: `M ${cx} ${cx} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      };
    });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} opacity="0.9" />
      ))}
      <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
      <text
        x={cx}
        y={cx + 4}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#1e293b"
      >
        {total}
      </text>
    </svg>
  );
};

const CandSparkBars = ({ items, colorClass }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="hfd-sparkbars">
      {items.map((item) => (
        <div key={item.label} className="hfd-spark-row">
          <span className="hfd-spark-row__label" title={item.label}>
            {item.label.length > 16
              ? item.label.slice(0, 15) + "…"
              : item.label}
          </span>
          <div className="hfd-spark-row__track">
            <div
              className={`hfd-spark-row__fill${colorClass ? ` hfd-spark-row__fill--${colorClass}` : ""}`}
              style={{ width: `${(item.value / max) * 100}%` }}
            />
          </div>
          <span className="hfd-spark-row__val">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

const CandSidebarWidget = ({ title, children, tipsVariant }) => (
  <div className={`hfd-widget${tipsVariant ? " hfd-widget--tips" : ""}`}>
    <p className="hfd-widget__title">{title}</p>
    {children}
  </div>
);

const SearchBar = ({ search, setSearch }) => (
  <div className="cand-search-wrap">
    <svg
      className="cand-search-icon"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <circle cx="6.5" cy="6.5" r="4.5" stroke="#9ca3af" strokeWidth="1.5" />
      <path
        d="M10.5 10.5l3 3"
        stroke="#9ca3af"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
    <input
      className="cand-search"
      placeholder="Search candidates..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
    {search && (
      <button className="cand-search-clear" onClick={() => setSearch("")}>
        ×
      </button>
    )}
  </div>
);

const SkillTags = ({ skills }) => {
  const fullSkills = (skills || "").trim();
  if (!fullSkills) {
    return (
      <div className="cand-skills">
        <span className="cand-na">—</span>
      </div>
    );
  }

  const skillList = fullSkills
    .split(",")
    .map((skill) => skill.trim())
    .filter(Boolean);
  const previewSkills = skillList.slice(0, 4);

  return (
    <div
      className="cand-skills cand-skills--compact"
      aria-label={`Skills: ${fullSkills}`}
    >
      <div className="cand-skills__preview">
        {previewSkills.map((skill) => (
          <span key={skill} className="cand-skill-tag">
            {skill}
          </span>
        ))}
        {skillList.length > 4 && (
          <span className="cand-skill-tag cand-skill-tag--more">
            +{skillList.length - 4}
          </span>
        )}
      </div>
      <span className="cand-skills__tooltip" role="tooltip">
        {fullSkills}
      </span>
    </div>
  );
};

const RecentSlotLink = ({ url }) => {
  const [copied, setCopied] = useState(false);

  if (!url) return <span className="cand-na">—</span>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Failed to copy slot link.");
    }
  };

  return (
    <div className="cand-slot-link">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="cand-slot-link__open"
        title={url}
      >
        Open link
      </a>
      <button
        type="button"
        className={`cand-slot-link__copy${copied ? " cand-slot-link__copy--done" : ""}`}
        onClick={handleCopy}
        title="Copy slot booking link"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const InterviewStatusBadge = ({ status }) => {
  const MAP = {
    PENDING: { label: "Pending", cls: "cand-istatus--pending" },
    SCHEDULED: { label: "Scheduled", cls: "cand-istatus--scheduled" },
    SHORTLISTED: { label: "Shortlisted", cls: "cand-istatus--shortlisted" },
    PROCESSING: { label: "Processing", cls: "cand-istatus--processing" },
    HOLD: { label: "Hold", cls: "cand-istatus--hold" },
    REJECTED: { label: "Rejected", cls: "cand-istatus--rejected" },
    SELECTED: { label: "Selected", cls: "cand-istatus--selected" },
  };
  const s = MAP[(status || "").toUpperCase()] || {
    label: status || "—",
    cls: "",
  };
  return <span className={`cand-istatus ${s.cls}`}>{s.label}</span>;
};

const DetailRow = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="cand-detail-row">
      <span className="cand-detail-row__icon">{icon}</span>
      <div>
        <div className="cand-detail-row__label">{label}</div>
        <div className="cand-detail-row__value">{value}</div>
      </div>
    </div>
  );
};

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 4h10M6 4V3h4v1M5 4v9h6V4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── BulkScheduleModal ────────────────────────────────────────────────────────
const MOVE_TO_INTERVIEW_API = `${process.env.REACT_APP_API_HR_URL}/candidate/move-to-interview`;

const BulkScheduleModal = ({ candidates, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // { successNames: [], failedNames: [] }

  // Fire immediately on mount — send all candidates in one batch
  useEffect(() => {
    const submit = async () => {
      setLoading(true);
      const payload = candidates.map((c) => ({
        appliedCandidateHistoryId:
          c.appliedCandidateHistoryId ?? c.appliedCandidateId,
        appliedCandidateHistorySourceId: c.sourceId ?? null,
        comments: "",
        status: "MOVED",
      }));

      try {
        await axios.put(MOVE_TO_INTERVIEW_API, payload);
        setResults({
          successNames: candidates.map((c) => c.candidateName),
          failedNames: [],
        });
        toast.success(
          `${candidates.length} candidate${candidates.length !== 1 ? "s" : ""} moved to interview.`,
        );
      } catch (err) {
        setResults({
          successNames: [],
          failedNames: candidates.map((c) => c.candidateName),
        });
        toast.error(
          err?.response?.data?.message ||
            "Failed to move candidates to interview.",
        );
      } finally {
        setLoading(false);
      }
    };

    submit();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="bulk-overlay"
      onClick={(e) => !loading && e.target === e.currentTarget && onClose()}
    >
      <div className="bulk-modal" style={{ maxWidth: 500 }}>
        <div className="bulk-modal__header">
          <span className="bulk-modal__title">
            Bulk Schedule Interview
            <span
              style={{
                marginLeft: 8,
                fontSize: "0.78em",
                fontWeight: 500,
                background: "#e0e7ff",
                color: "#4338ca",
                padding: "2px 8px",
                borderRadius: 20,
              }}
            >
              {candidates.length} candidate{candidates.length !== 1 ? "s" : ""}
            </span>
          </span>
          {!loading && (
            <button className="bulk-modal__close" onClick={onClose}>
              ✕
            </button>
          )}
        </div>

        {loading ? (
          <div
            className="bulk-modal__body"
            style={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              style={{ animation: "spin 0.9s linear infinite" }}
            >
              <circle cx="16" cy="16" r="13" stroke="#e2e8f0" strokeWidth="3" />
              <path
                d="M16 3a13 13 0 0 1 13 13"
                stroke="#94a3b8"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <span style={{ color: "#64748b", fontSize: "0.875rem" }}>
              Moving {candidates.length} candidate
              {candidates.length !== 1 ? "s" : ""} to interview…
            </span>
          </div>
        ) : results ? (
          <>
            <div className="bulk-modal__body">
              {/* Candidate chips summary */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  marginBottom: 12,
                }}
              >
                {candidates.map((c) => (
                  <span
                    key={c.appliedCandidateId}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: results.failedNames.includes(c.candidateName)
                        ? "#fee2e2"
                        : "#dcfce7",
                      fontSize: "0.8rem",
                      color: results.failedNames.includes(c.candidateName)
                        ? "#991b1b"
                        : "#166534",
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: avatarColor(c.candidateName),
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.65rem",
                        color: "#fff",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(c.candidateName)}
                    </span>
                    {c.candidateName}
                  </span>
                ))}
              </div>

              {results.successNames.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 8,
                    marginBottom: results.failedNames.length > 0 ? 8 : 0,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#16a34a"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M5 8l2 2 4-4"
                      stroke="#16a34a"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span
                    style={{
                      color: "#15803d",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {results.successNames.length} candidate
                    {results.successNames.length !== 1 ? "s" : ""} successfully
                    moved to interview
                  </span>
                </div>
              )}

              {results.failedNames.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 14px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="#dc2626"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M6 6l4 4M10 6l-4 4"
                      stroke="#dc2626"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span
                    style={{
                      color: "#b91c1c",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {results.failedNames.length} failed:{" "}
                    {results.failedNames.join(", ")}
                  </span>
                </div>
              )}
            </div>
            <div className="bulk-modal__footer">
              <button
                className="bulk-btn-upload bulk-btn-upload--ready"
                onClick={() => {
                  onDone();
                  onClose();
                }}
              >
                Done
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default AllCandidates;
